import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import TiptapImage from '@tiptap/extension-image';
import TiptapLink from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import TiptapUnderline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
    AlignCenter,
    AlignJustify,
    AlignLeft,
    AlignRight,
    BarChart3,
    Bold,
    Calendar,
    CheckCircle,
    Code,
    FileText,
    Heading1,
    Heading2,
    Heading3,
    Highlighter,
    Image as ImageIcon,
    Italic,
    Link as LinkIcon,
    List,
    ListOrdered,
    Loader2,
    Paintbrush,
    Plus,
    Quote,
    Redo,
    Save,
    Sparkles,
    Strikethrough,
    TrendingUp,
    Type,
    Underline as UnderlineIcon,
    Undo,
    Upload,
    X
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import blogAPI from '../../services/blogAPI';
import subscriptionService from '../../services/subscriptionService';
import './BlogPostEditor.css';

// Helper to get full image URL
const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    //TODO: remove this hardcoded API URL later
    // Hardcoded API URL based on hostname
    const hostname = window.location.hostname;
    let API_BASE_URL;

    // Production domains
    if (hostname === 'lima.ac' || hostname.includes('lima.ac')) {
        API_BASE_URL = 'https://lima.ac';
    } else if (hostname === 'lima.pitech.ca' || hostname.includes('lima.pitech.ca')) {
        API_BASE_URL = 'https://lima.pitech.ca';
    } else if (hostname === 'bagbox.ca' || hostname.includes('bagbox.ca')) {
        API_BASE_URL = 'https://bagbox.ca';
    } else {
        // Development/localhost
        API_BASE_URL = 'http://localhost:80';
    }

    return `${API_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
};

const BlogPostEditor = ({ post, categories = [], onSave, onClose, onCategoryCreated }) => {
    // UI State
    const [saving, setSaving] = useState(false);
    const [showImagePicker, setShowImagePicker] = useState(false);
    const [showMediaGallery, setShowMediaGallery] = useState(false);
    const [imagePickerTarget, setImagePickerTarget] = useState('featured');
    const [showAIModal, setShowAIModal] = useState(false);
    const [showSEOAnalysis, setShowSEOAnalysis] = useState(false);
    const [showComprehensiveAnalysis, setShowComprehensiveAnalysis] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [showImageUploadOptions, setShowImageUploadOptions] = useState(false);
    const [imageUrl, setImageUrl] = useState('');
    const [featuredImageTab, setFeaturedImageTab] = useState('upload');
    const [aiImagePrompt, setAiImagePrompt] = useState('');
    const [aiPrompt, setAIPrompt] = useState('');

    // Generation states
    const [generatingAI, setGeneratingAI] = useState(false);
    const [generatingContent, setGeneratingContent] = useState(false);
    const [generatingExcerpt, setGeneratingExcerpt] = useState(false);
    const [generatingSEOTitle, setGeneratingSEOTitle] = useState(false);
    const [generatingTags, setGeneratingTags] = useState(false);
    const [generatingImage, setGeneratingImage] = useState(false);
    const [analyzingSEO, setAnalyzingSEO] = useState(false);
    const [analyzingSEOInModal, setAnalyzingSEOInModal] = useState(false);
    const [analyzingComprehensive, setAnalyzingComprehensive] = useState(false);
    const [analyzingGeneratedPost, setAnalyzingGeneratedPost] = useState(false);
    const [improvingContent, setImprovingContent] = useState(false);

    // Analysis states
    const [seoAnalysis, setSeoAnalysis] = useState(null);
    const [seoAnalysisModal, setSeoAnalysisModal] = useState(null);
    const [comprehensiveAnalysis, setComprehensiveAnalysis] = useState(null);
    const [generatedPostAnalysis, setGeneratedPostAnalysis] = useState(null);
    const [improvingMetric, setImprovingMetric] = useState(null);
    const [metricImprovements, setMetricImprovements] = useState({});

    // Schedule states
    const [scheduleDate, setScheduleDate] = useState('');
    const [scheduleTime, setScheduleTime] = useState('');

    // Generated post states
    const [generatedPostPreview, setGeneratedPostPreview] = useState(null);
    const [lastAcceptedPost, setLastAcceptedPost] = useState(null);

    // Subscription states
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    const [subscriptionMessage, setSubscriptionMessage] = useState('');
    const [quotaExceededType, setQuotaExceededType] = useState('');
    const [isAIGenerated, setIsAIGenerated] = useState(false);

    // Image regeneration state
    const [imageHistory, setImageHistory] = useState({});
    const [regeneratingImage, setRegeneratingImage] = useState(null);
    const contentPreviewRef = useRef(null);
    const [imageHistoryModal, setImageHistoryModal] = useState(null);

    // Category creation states
    const [showInlineCategoryForm, setShowInlineCategoryForm] = useState(false);
    const [inlineCategoryName, setInlineCategoryName] = useState('');
    const [creatingInlineCategory, setCreatingInlineCategory] = useState(false);

    // Editor UI states
    const [showLinkInput, setShowLinkInput] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const [showImageInput, setShowImageInput] = useState(false);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [showHighlightPicker, setShowHighlightPicker] = useState(false);

    const [formData, setFormData] = useState({
        title: post?.title || '',
        slug: post?.slug || '',
        content: post?.content || '',
        excerpt: post?.excerpt || '',
        category: post?.categories?.[0]?._id || '',
        tags: post?.tags || [],
        featuredImage: post?.featuredImage?.url || post?.featuredImage || '',
        status: post?.status || 'draft',
        featured: post?.featured || false,
        scheduledFor: post?.scheduledFor || null,
        seo: {
            metaTitle: post?.seo?.metaTitle || '',
            metaDescription: post?.seo?.metaDescription || '',
            keywords: post?.seo?.keywords || [],
            ogImage: post?.seo?.ogImage || '',
            canonicalUrl: post?.seo?.canonicalUrl || ''
        }
    });

    // Update formData when post prop changes (for editing existing posts)
    useEffect(() => {
        if (post) {
            console.log('📝 Post data changed, updating formData:', {
                id: post._id,
                title: post.title,
                featuredImage: post.featuredImage,
                category: post.categories?.[0]?._id
            });

            setFormData({
                title: post.title || '',
                slug: post.slug || '',
                content: post.content || '',
                excerpt: post.excerpt || '',
                category: post.categories?.[0]?._id || '',
                tags: post.tags || [],
                featuredImage: post.featuredImage?.url || post.featuredImage || '',
                status: post.status || 'draft',
                featured: post.featured || false,
                scheduledFor: post.scheduledFor || null,
                seo: {
                    metaTitle: post.seo?.metaTitle || '',
                    metaDescription: post.seo?.metaDescription || '',
                    keywords: post.seo?.keywords || [],
                    ogImage: post.seo?.ogImage || '',
                    canonicalUrl: post.seo?.canonicalUrl || ''
                }
            });
        } else {
            // Reset form for new post
            console.log('📝 Creating new post, resetting formData');
            setFormData({
                title: '',
                slug: '',
                content: '',
                excerpt: '',
                category: '',
                tags: [],
                featuredImage: '',
                status: 'draft',
                featured: false,
                scheduledFor: null,
                seo: {
                    metaTitle: '',
                    metaDescription: '',
                    keywords: [],
                    ogImage: '',
                    canonicalUrl: ''
                }
            });
        }
    }, [post]);

    // Debug: Log when post data is loaded
    useEffect(() => {
        if (post) {
            console.log('📝 BlogPostEditor loaded with post:', {
                id: post._id,
                title: post.title,
                tagsCount: post.tags?.length || 0,
                tags: post.tags
            });
        }
    }, [post]);

    const [newTag, setNewTag] = useState('');
    const [newKeyword, setNewKeyword] = useState('');

    // Helper function to check quotas before AI operations
    const checkQuota = async (action) => {
        try {
            const quotaCheck = await subscriptionService.canPerformAIAction(action);

            if (!quotaCheck.allowed) {
                // If it's not an upgrade issue (server error, auth error, etc.), show simple alert
                if (!quotaCheck.requiresUpgrade) {
                    alert(quotaCheck.reason);
                    return false;
                }

                // If it's a quota/upgrade issue, show the subscription modal
                setSubscriptionMessage(quotaCheck.reason);
                setQuotaExceededType(action);
                setShowSubscriptionModal(true);
                return false;
            }

            return true;
        } catch (error) {
            console.error('Error checking quota:', error);
            alert('Unable to check your AI quota. Please try again.');
            return false;
        }
    };

    // Auto-generate slug from title
    useEffect(() => {
        if (!post && formData.title) {
            const slug = formData.title
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/(^-|-$)/g, '');
            setFormData(prev => ({ ...prev, slug }));
        }
    }, [formData.title, post]);

    // Attach click handlers to regenerate buttons in the preview
    useEffect(() => {
        if (!contentPreviewRef.current || !generatedPostPreview) return;

        const handleButtonClick = (event) => {
            const target = event.target;
            const button = target.closest('button');

            if (!button) return;

            const container = button.closest('[data-image-key]');
            if (!container) return;

            const imageKey = container.getAttribute('data-image-key');
            if (!imageKey) return;

            if (button.classList.contains('regenerate-btn')) {
                event.preventDefault();
                const prompt = generatedPostPreview.imagePrompts?.[imageKey] || `Image for ${imageKey}`;
                console.log('🔄 Regenerate clicked:', imageKey, prompt);
                handleRegenerateImage(imageKey, prompt);
            } else if (button.classList.contains('history-btn')) {
                event.preventDefault();
                console.log('📜 History clicked:', imageKey);
                const history = imageHistory[imageKey];
                const currentImageUrl = getCurrentImageUrl(imageKey);

                console.log('📊 History data:', {
                    imageKey,
                    historyLength: history?.length || 0,
                    currentImageUrl: currentImageUrl?.substring(0, 50) + '...',
                    fullHistory: history
                });

                if (history && history.length > 0 && currentImageUrl) {
                    // Open modal with image history
                    setImageHistoryModal({
                        isOpen: true,
                        imageKey,
                        currentImageUrl
                    });
                    console.log('✅ Modal opened');
                } else {
                    alert(`No previous versions available for this image.`);
                    console.log('⚠️ No history available');
                }
            }
        };

        contentPreviewRef.current.addEventListener('click', handleButtonClick);

        return () => {
            if (contentPreviewRef.current) {
                contentPreviewRef.current.removeEventListener('click', handleButtonClick);
            }
        };
    }, [generatedPostPreview, imageHistory, regeneratingImage]);

    // Initialize Tiptap editor
    const editor = useEditor({
        extensions: [
            StarterKit,
            TiptapUnderline,
            TiptapImage.configure({
                inline: true,
                allowBase64: false,
            }),
            TiptapLink.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: 'text-blue-600 underline',
                },
            }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            TextStyle,
            Color,
            Highlight.configure({
                multicolor: true,
            }),
        ],
        content: formData.content || '',
        editorProps: {
            attributes: {
                class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-xl focus:outline-none min-h-[400px] max-w-none p-4',
            },
        },
        onUpdate: ({ editor }) => {
            setFormData(prev => ({
                ...prev,
                content: editor.getHTML()
            }));
        },
    });

    // Update editor content when formData.content changes externally (e.g., AI generation)
    useEffect(() => {
        if (editor && formData.content && formData.content !== editor.getHTML()) {
            editor.commands.setContent(formData.content);
        }
    }, [formData.content, editor]);

    // Auto-generate slug from title
    useEffect(() => {
        if (!post && formData.title) {
            const slug = formData.title
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/(^-|-$)/g, '');
            setFormData(prev => ({
                ...prev,
                seo: { ...prev.seo, slug }
            }));
        }
    }, [formData.title, post]);

    // Tiptap helper functions
    const addImage = useCallback(() => {
        if (imageUrl && editor) {
            editor.chain().focus().setImage({ src: imageUrl }).run();
            setImageUrl('');
            setShowImageInput(false);
        }
    }, [imageUrl, editor]);

    const addLink = useCallback(() => {
        if (linkUrl && editor) {
            editor.chain().focus().setLink({ href: linkUrl }).run();
            setLinkUrl('');
            setShowLinkInput(false);
        }
    }, [linkUrl, editor]);

    const setColor = (color) => {
        editor?.chain().focus().setColor(color).run();
        setShowColorPicker(false);
    };

    const setHighlight = (color) => {
        editor?.chain().focus().setHighlight({ color }).run();
        setShowHighlightPicker(false);
    };

    const colors = [
        '#000000', '#e60000', '#ff9900', '#ffff00', '#008a00', '#0066cc', '#9933ff',
        '#ffffff', '#facccc', '#ffebcc', '#ffffcc', '#cce8cc', '#cce0f5', '#ebd6ff',
        '#bbbbbb', '#f06666', '#ffc266', '#ffff66', '#66b966', '#66a3e0', '#c285ff',
        '#888888', '#a10000', '#b26b00', '#b2b200', '#006100', '#0047b2', '#6b24b2',
        '#444444', '#5c0000', '#663d00', '#666600', '#003700', '#002966', '#3d1466',
    ];

    // Handle AI Generate Post button click
    const handleAIGenerateClick = async () => {
        // Check AI post quota before opening modal
        const canGenerate = await checkQuota('post');
        if (!canGenerate) {
            return; // Modal will be shown automatically by checkQuota
        }
        setShowAIModal(true);
    };

    // Helper function to get regex for finding a SPECIFIC image by key
    const getImageRegex = (imageKey) => {
        // Map imageKey to figure position
        const figureMap = {
            'hero': 0,
            'img1': 1,
            'img2': 2,
            'img3': 3,
            'img4': 4,
            'img5': 5,
            'img6': 6,
            'img7': 7
        };

        const targetIndex = figureMap[imageKey];
        if (targetIndex === undefined) return /<figure[^>]*>[\s\S]*?<\/figure>/;

        // This regex will match the specific figure at the target index
        // We'll use this with a counter in replace function
        return new RegExp('<figure[^>]*>[\\s\\S]*?<\\/figure>', 'g');
    };

    // Replace image at specific index
    const replaceImageAtIndex = (content, imageKey, newImageUrl) => {
        const figureMap = {
            'hero': 0,
            'img1': 1,
            'img2': 2,
            'img3': 3,
            'img4': 4,
            'img5': 5,
            'img6': 6,
            'img7': 7
        };

        const targetIndex = figureMap[imageKey];
        if (targetIndex === undefined) return content;

        let currentIndex = 0;
        const regex = /<figure[^>]*>[\s\S]*?<\/figure>/g;

        return content.replace(regex, (match) => {
            if (currentIndex === targetIndex) {
                // This is the figure we want to replace
                currentIndex++;
                return match.replace(/src="[^"]*"/, `src="${newImageUrl}"`);
            }
            currentIndex++;
            return match; // Keep other figures unchanged
        });
    };

    // Get current image URL for a specific key
    const getCurrentImageUrl = (imageKey) => {
        const figureMap = {
            'hero': 0,
            'img1': 1,
            'img2': 2,
            'img3': 3,
            'img4': 4,
            'img5': 5,
            'img6': 6,
            'img7': 7
        };

        const targetIndex = figureMap[imageKey];
        if (targetIndex === undefined || !generatedPostPreview) return null;

        const regex = /<figure[^>]*>[\s\S]*?<\/figure>/g;
        const matches = generatedPostPreview.content.match(regex);

        if (!matches || !matches[targetIndex]) return null;

        const imgMatch = matches[targetIndex].match(/src="([^"]*)"/);
        return imgMatch ? imgMatch[1] : null;
    };

    // Restore previous image version
    const handleRestoreImageVersion = (imageKey, imageUrl) => {
        // Save current image to history BEFORE restoring the old one
        const currentImageUrl = getCurrentImageUrl(imageKey);
        if (currentImageUrl && currentImageUrl !== imageUrl) {
            setImageHistory(prev => ({
                ...prev,
                [imageKey]: [...(prev[imageKey] || []), currentImageUrl]
            }));
            console.log(`💾 Saved current image to history before restore: ${imageKey}`);
        }

        const updatedContent = replaceImageAtIndex(
            generatedPostPreview.content,
            imageKey,
            imageUrl
        );

        setGeneratedPostPreview({
            ...generatedPostPreview,
            content: updatedContent
        });

        setImageHistoryModal(null); // Close modal
        console.log(`✅ Image restored: ${imageKey} -> ${imageUrl}`);
    };

    // Regenerate individual image in generated post
    const handleRegenerateImage = async (imageKey, imagePrompt) => {
        // Check image generation quota
        const canGenerate = await checkQuota('image');
        if (!canGenerate) {
            return;
        }

        setRegeneratingImage(imageKey);
        try {
            console.log(`🔄 Regenerating image: ${imageKey}`);

            // Save current image to history BEFORE generating new one
            const currentImageUrl = getCurrentImageUrl(imageKey);
            if (currentImageUrl) {
                setImageHistory(prev => ({
                    ...prev,
                    [imageKey]: [...(prev[imageKey] || []), currentImageUrl]
                }));
                console.log(`💾 Saved current image to history for ${imageKey}`);
            }

            // Generate new image
            const response = await blogAPI.generateImage({
                prompt: imagePrompt,
                options: { size: '1792x1024', quality: 'hd', style: 'natural' },
                skipUsageTracking: true // Skip automatic tracking - we'll manually record below
            });

            if (response.data.success && response.data.imageUrl) {
                const newImageUrl = getImageUrl(response.data.imageUrl);
                console.log(`✅ New image generated: ${newImageUrl}`);

                // Record quota usage
                await blogAPI.recordAIUsage({
                    action: 'image',
                    data: {
                        type: 'ai-regenerated-blog-image',
                        imageKey,
                        generatedAt: new Date().toISOString()
                    }
                });

                // Replace ONLY this specific image in content
                const updatedContent = replaceImageAtIndex(
                    generatedPostPreview.content,
                    imageKey,
                    newImageUrl
                );

                console.log(`✅ Replaced ${imageKey} with new image`);

                // Update preview
                setGeneratedPostPreview({
                    ...generatedPostPreview,
                    content: updatedContent
                });

            } else {
                alert('Failed to generate image. Please try again.');
            }
        } catch (error) {
            console.error('Error regenerating image:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';

            if (error?.response?.status === 429) {
                alert(`❌ Quota exceeded: ${errorMessage}`);
            } else {
                alert(`Failed to regenerate image: ${errorMessage}`);
            }
        } finally {
            setRegeneratingImage(null);
        }
    };

    const handleSave = async (status = formData.status) => {
        if (!formData.title || !formData.content) {
            alert('Title and content are required');
            return;
        }

        setSaving(true);
        try {
            console.log('💾 Saving post with formData:', {
                title: formData.title,
                featuredImage: formData.featuredImage,
                category: formData.category
            });

            const postData = {
                ...formData,
                status: status || formData.status,
                categories: formData.category ? [formData.category] : [],
                featuredImage: formData.featuredImage ? {
                    url: formData.featuredImage,
                    alt: formData.title
                } : undefined
                // Removed aiGenerated flag - quota is already recorded when user accepts AI post
            };

            console.log('📤 Sending postData:', postData);

            await onSave(postData);

            // Reset AI generated flag
            setIsAIGenerated(false);
            onClose();
        } catch (error) {
            console.error('Error saving post:', error);
            alert('Failed to save post');
        } finally {
            setSaving(false);
        }
    };

    const handleAddTag = () => {
        if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, newTag.trim()]
            }));
            setNewTag('');
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    const handleAddKeyword = () => {
        if (newKeyword.trim() && !formData.seo.keywords.includes(newKeyword.trim())) {
            setFormData(prev => ({
                ...prev,
                seo: {
                    ...prev.seo,
                    keywords: [...prev.seo.keywords, newKeyword.trim()]
                }
            }));
            setNewKeyword('');
        }
    };

    const handleRemoveKeyword = (keywordToRemove) => {
        setFormData(prev => ({
            ...prev,
            seo: {
                ...prev.seo,
                keywords: prev.seo.keywords.filter(kw => kw !== keywordToRemove)
            }
        }));
    };

    const handleFileUpload = async (file) => {
        if (!file) return;

        try {
            // Show blob URL immediately for preview
            const objectUrl = URL.createObjectURL(file);
            setFormData(prev => ({ ...prev, featuredImage: objectUrl }));

            // Upload to server
            const formDataToUpload = new FormData();
            formDataToUpload.append('file', file);

            const response = await blogAPI.uploadMedia(formDataToUpload);

            if (response.data && response.data.url) {
                // Replace blob URL with server URL
                setFormData(prev => ({ ...prev, featuredImage: response.data.url }));
                // Clean up blob URL
                URL.revokeObjectURL(objectUrl);
                console.log('✅ Image uploaded successfully:', response.data.url);
            }
        } catch (error) {
            console.error('❌ Error uploading image:', error);
            alert('Failed to upload image. Please try again.');
            setFormData(prev => ({ ...prev, featuredImage: '' }));
        }
    };

    const handleImageUrlAdd = () => {
        if (imageUrl.trim()) {
            setFormData(prev => ({
                ...prev,
                featuredImage: imageUrl.trim()
            }));
            setImageUrl('');
        }
    };

    const handleGenerateFeaturedImage = async (prompt) => {
        // Check image generation quota
        const canGenerate = await checkQuota('image');
        if (!canGenerate) {
            return;
        }

        setGeneratingImage(true);
        try {
            console.log('🎨 Generating featured image with prompt:', prompt);

            const response = await blogAPI.generateImage({
                prompt: prompt || aiImagePrompt,
                options: { size: '1792x1024', quality: 'hd', style: 'natural' },
                skipUsageTracking: true
            });

            console.log('📦 Full response:', response);
            console.log('📦 Response data:', response.data);

            // Extract imageUrl from response
            let imageUrl = response.data.imageUrl || response.data.url;

            console.log('✅ Extracted imageUrl:', imageUrl);

            if (imageUrl) {
                console.log('✅ Setting featured image to:', imageUrl);

                // Update form data with the image URL
                setFormData(prev => {
                    const updated = { ...prev, featuredImage: imageUrl };
                    console.log('📝 Updated formData:', updated);
                    return updated;
                });

                // Clear the prompt input
                setAiImagePrompt('');

                // Switch to upload tab to show the preview
                setFeaturedImageTab('upload');

                // Record AI usage
                try {
                    await blogAPI.recordAIUsage({
                        action: 'image',
                        data: { prompt, type: 'featured' }
                    });
                    console.log('✅ AI image usage recorded successfully');
                } catch (usageError) {
                    console.error('Failed to record usage:', usageError);
                }

                // Show success message
                alert('✅ Featured image generated successfully! You can now save your post.');
            } else {
                console.error('❌ No image URL found in response');
                console.error('Response data:', response.data);
                alert('Image generated but URL not found in response. Please try again.');
            }
        } catch (error) {
            console.error('❌ Error generating featured image:', error);

            // Provide detailed error message
            let errorMessage = 'Failed to generate image. ';

            if (error.response?.data?.details) {
                errorMessage += error.response.data.details;
            } else if (error.response?.data?.message) {
                errorMessage += error.response.data.message;
            } else if (error.message) {
                errorMessage += error.message;
            } else {
                errorMessage += 'Please check your API configuration and try again.';
            }

            alert(errorMessage);
        } finally {
            setGeneratingImage(false);
        }
    };

    // AI Generate Full Post - Professional Blog Post with 7 images
    const handleAIGeneratePost = async () => {
        if (!aiPrompt.trim()) {
            alert('Please describe the post you want to create');
            return;
        }

        setGeneratingAI(true);
        try {
            // Extract key concepts for image generation
            const concepts = aiPrompt.split(' ').filter(w => w.length > 4);
            const mainConcept = concepts.slice(0, 3).join(' ');

            // Step 1: Generate title suggestions
            const titleResponse = await blogAPI.generateTitles({ topic: aiPrompt, count: 1, skipUsageTracking: true });
            const generatedTitle = titleResponse.data.titles && titleResponse.data.titles.length > 0
                ? titleResponse.data.titles[0]
                : `${mainConcept.split(' ').map(w =>
                    w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
                ).join(' ')}: A Complete Guide for ${new Date().getFullYear()}`;

            // Step 2: Generate comprehensive blog content with PRACTICAL, SIMPLIFIED Prompt
            const currentYear = new Date().getFullYear();

            const contentResponse = await blogAPI.generateContent({
                skipUsageTracking: true,
                useRawPrompt: true, // Use frontend prompt directly without backend modifications
                prompt: `You are an expert SEO content writer. Create a comprehensive, engaging blog post about: "${aiPrompt}"

TARGET: Write a 2,000-2,500 word article that ranks well on Google and provides genuine value to readers.

CONTENT GUIDELINES (not strict rules):

1. WRITING STYLE:
- Write naturally and conversationally - sound human, not robotic
- Use "you" to connect with readers personally  
- Mix sentence lengths for rhythm (short punches + longer explanations)
- Break up text with lists, subheadings, examples
- Keep paragraphs short (2-4 sentences)

2. SEO BASICS:
- Include the main keyword naturally in: title, intro, one subheading, conclusion
- Don't force or stuff keywords - write for humans first
- Answer common questions clearly
- Use descriptive subheadings

3. CONTENT TO INCLUDE:
- Start with a strong hook that grabs attention
- Introduction: What's the problem? Why does it matter?
- Core sections covering the topic thoroughly
- Practical examples and actionable advice
- Common mistakes/pitfalls section
- Brief conclusion with next steps

4. HELPFUL ADDITIONS (when relevant):
- Real-world examples (can use "Company X" if needed)
- Statistics and data points (use realistic ranges like "studies show 30-50%" rather than inventing exact numbers)
- Practical tips and shortcuts
- Tool recommendations

5. IMAGE MARKERS:
Place these 7 markers strategically throughout your content:
[HERO_IMAGE] - after introduction
[IMAGE_1], [IMAGE_2], [IMAGE_3], [IMAGE_4], [IMAGE_5], [IMAGE_6], [IMAGE_7] - spread evenly through main sections

6. FORMATTING (use inline styles):
- H1 for title: <h1 style="font-size: 42px; color: #0f172a; font-weight: 700; margin-bottom: 24px;">[Title]</h1>
- H2 for sections: <h2 style="font-size: 32px; color: #0f172a; font-weight: 600; margin-top: 64px; margin-bottom: 20px;">[Section]</h2>
- H3 for subsections: <h3 style="font-size: 24px; color: #1e293b; font-weight: 500; margin-top: 48px; margin-bottom: 16px;">[Subsection]</h3>
- Regular paragraphs: <p style="font-size: 17px; color: #475569; line-height: 1.9; margin-bottom: 20px;">[Content]</p>

IMPORTANT FORMATTING RULES:
- DO NOT use emojis anywhere in the content (no 💡, 🎯, 🚀, etc.)
- For image markers, use ONLY these exact formats with NO additional text:
  [HERO_IMAGE]
  [IMAGE_1]
  [IMAGE_2]
  [IMAGE_3]
  [IMAGE_4]
  [IMAGE_5]
  [IMAGE_6]
  [IMAGE_7]
- Do NOT add descriptions, alt text, or any other text to image markers
- Do NOT write [IMAGE_1: description] or [IMAGE_1 | Alt: text] - just write [IMAGE_1]

END NOTE:
Focus on creating genuinely helpful content that solves the reader's problem. Write naturally - don't try to hit exact numbers or percentages. Quality over checklist completion. Remember: NO EMOJIS and use simple image markers only.

Now write a comprehensive, engaging, 2,000-2,500 word blog post:`,
                tone: 'professional',
                length: 'long'
            });

            let generatedContent = contentResponse.data.content || '';

            // Remove markdown code block syntax if present (```html ... ``` or ``` ... ```)
            generatedContent = generatedContent
                .replace(/^```html\s*/i, '')  // Remove opening ```html
                .replace(/^```\s*/, '')        // Remove opening ``` 
                .replace(/\s*```\s*$/, '')     // Remove closing ```
                .trim();

            console.log('🎨 Analyzing content to create contextually relevant image prompts...');

            // Parse the HTML content to extract section-specific context for each image
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = generatedContent;
            const mainTopic = aiPrompt.trim();

            // Function to extract context around each image marker
            const extractSectionContext = (content, markerPattern) => {
                const markerIndex = content.search(markerPattern);
                if (markerIndex === -1) return null;

                // Get content before the marker (up to 1500 chars to find the section)
                const contentBefore = content.substring(Math.max(0, markerIndex - 1500), markerIndex);

                // Find the last heading (h2 or h3) before the marker
                const headingMatch = contentBefore.match(/<h[23][^>]*>([^<]+)<\/h[23]>/gi);
                const lastHeading = headingMatch ? headingMatch[headingMatch.length - 1].replace(/<[^>]+>/g, '').trim() : '';

                // Find paragraphs between the heading and the marker
                const lastHeadingIndex = lastHeading ? contentBefore.lastIndexOf(lastHeading) : 0;
                const contentAfterHeading = contentBefore.substring(lastHeadingIndex);
                const paragraphMatches = contentAfterHeading.match(/<p[^>]*>([^<]+)<\/p>/gi) || [];
                const paragraphText = paragraphMatches
                    .map(p => p.replace(/<[^>]+>/g, '').trim())
                    .filter(p => p.length > 30)
                    .slice(0, 2)
                    .join(' ')
                    .substring(0, 200);

                return {
                    heading: lastHeading,
                    context: paragraphText || lastHeading || mainTopic
                };
            };

            // Extract context for each image marker
            const heroContext = extractSectionContext(generatedContent, /\[HERO_IMAGE/i) || { heading: mainTopic, context: mainTopic };
            const img1Context = extractSectionContext(generatedContent, /\[IMAGE_1/i) || { heading: '', context: mainTopic };
            const img2Context = extractSectionContext(generatedContent, /\[IMAGE_2/i) || { heading: '', context: mainTopic };
            const img3Context = extractSectionContext(generatedContent, /\[IMAGE_3/i) || { heading: '', context: mainTopic };
            const img4Context = extractSectionContext(generatedContent, /\[IMAGE_4/i) || { heading: '', context: mainTopic };
            const img5Context = extractSectionContext(generatedContent, /\[IMAGE_5/i) || { heading: '', context: mainTopic };
            const img6Context = extractSectionContext(generatedContent, /\[IMAGE_6/i) || { heading: '', context: mainTopic };
            const img7Context = extractSectionContext(generatedContent, /\[IMAGE_7/i) || { heading: '', context: mainTopic };

            console.log('📝 Extracted section contexts for image generation:', {
                hero: heroContext,
                img1: img1Context,
                img2: img2Context,
                img3: img3Context,
                img4: img4Context,
                img5: img5Context,
                img6: img6Context,
                img7: img7Context
            });

            // Generate 7 section-specific, contextually relevant images
            const createImagePrompt = (sectionContext, mainTopic, fallbackDesc) => {
                const subject = sectionContext.heading || sectionContext.context || fallbackDesc;
                const context = sectionContext.context || mainTopic;
                return `A professional, high-quality photograph about: "${subject}". Context: ${context.substring(0, 150)}. Related to the topic of ${mainTopic}. Show the subject matter clearly and realistically. Modern, clean composition with good lighting. Photorealistic style. No text, logos, or graphics on the image.`;
            };

            const heroImagePrompt = `A professional, high-quality hero photograph representing: ${mainTopic}. ${heroContext.context ? `Context: ${heroContext.context.substring(0, 150)}.` : ''} Show the main subject clearly and compellingly. Modern, cinematic composition with excellent lighting. Ultra-realistic, magazine-quality photography. Wide 16:9 format. No text or graphics.`;

            const image1Prompt = createImagePrompt(img1Context, mainTopic, `introduction to ${mainTopic}`);
            const image2Prompt = createImagePrompt(img2Context, mainTopic, `${mainTopic} concepts`);
            const image3Prompt = createImagePrompt(img3Context, mainTopic, `${mainTopic} in practice`);
            const image4Prompt = createImagePrompt(img4Context, mainTopic, `${mainTopic} strategies`);
            const image5Prompt = createImagePrompt(img5Context, mainTopic, `${mainTopic} examples`);
            const image6Prompt = createImagePrompt(img6Context, mainTopic, `${mainTopic} tools and resources`);
            const image7Prompt = createImagePrompt(img7Context, mainTopic, `future of ${mainTopic}`);

            // Log the prompts for debugging
            console.log('🖼️ Image prompts created for 7 images:');
            console.log('Hero:', heroImagePrompt.substring(0, 100) + '...');
            console.log('Img 1:', image1Prompt.substring(0, 100) + '...');
            console.log('Img 2:', image2Prompt.substring(0, 100) + '...');
            console.log('Img 3:', image3Prompt.substring(0, 100) + '...');
            console.log('Img 4:', image4Prompt.substring(0, 100) + '...');
            console.log('Img 5:', image5Prompt.substring(0, 100) + '...');
            console.log('Img 6:', image6Prompt.substring(0, 100) + '...');
            console.log('Img 7:', image7Prompt.substring(0, 100) + '...');

            // Generate all 7 images in parallel for best performance
            console.log('🎨 Starting generation of 7 ultra-realistic, contextually relevant photographs...');

            const imageOptions = { size: '1792x1024', quality: 'hd', style: 'natural' };

            const imagePromises = [
                blogAPI.generateImage({ prompt: heroImagePrompt, options: imageOptions, skipUsageTracking: true })
                    .then(res => { console.log('✅ Hero image generated'); return res; })
                    .catch(err => { console.error('❌ Hero image failed:', err.message); return null; }),
                blogAPI.generateImage({ prompt: image1Prompt, options: imageOptions, skipUsageTracking: true })
                    .then(res => { console.log('✅ Image 1 generated'); return res; })
                    .catch(err => { console.error('❌ Image 1 failed:', err.message); return null; }),
                blogAPI.generateImage({ prompt: image2Prompt, options: imageOptions, skipUsageTracking: true })
                    .then(res => { console.log('✅ Image 2 generated'); return res; })
                    .catch(err => { console.error('❌ Image 2 failed:', err.message); return null; }),
                blogAPI.generateImage({ prompt: image3Prompt, options: imageOptions, skipUsageTracking: true })
                    .then(res => { console.log('✅ Image 3 generated'); return res; })
                    .catch(err => { console.error('❌ Image 3 failed:', err.message); return null; }),
                blogAPI.generateImage({ prompt: image4Prompt, options: imageOptions, skipUsageTracking: true })
                    .then(res => { console.log('✅ Image 4 generated'); return res; })
                    .catch(err => { console.error('❌ Image 4 failed:', err.message); return null; }),
                blogAPI.generateImage({ prompt: image5Prompt, options: imageOptions, skipUsageTracking: true })
                    .then(res => { console.log('✅ Image 5 generated'); return res; })
                    .catch(err => { console.error('❌ Image 5 failed:', err.message); return null; }),
                blogAPI.generateImage({ prompt: image6Prompt, options: imageOptions, skipUsageTracking: true })
                    .then(res => { console.log('✅ Image 6 generated'); return res; })
                    .catch(err => { console.error('❌ Image 6 failed:', err.message); return null; }),
                blogAPI.generateImage({ prompt: image7Prompt, options: imageOptions, skipUsageTracking: true })
                    .then(res => { console.log('✅ Image 7 generated'); return res; })
                    .catch(err => { console.error('❌ Image 7 failed:', err.message); return null; })
            ];

            const [heroImageRes, image1Res, image2Res, image3Res, image4Res, image5Res, image6Res, image7Res] = await Promise.all(imagePromises);

            // Helper function to extract image URL from response (handles imageUrl vs imageURL)
            const extractImageUrl = (response) => {
                if (!response || !response.data) return '';

                try {
                    // Use JSON round-trip to handle Proxy/getter issues
                    const jsonString = JSON.stringify(response.data);
                    const cleanData = JSON.parse(jsonString);

                    // Check for imageUrl (lowercase), imageURL (uppercase), or url
                    return cleanData.imageUrl || cleanData.imageURL || cleanData.url || '';
                } catch (e) {
                    console.error('Error extracting image URL:', e);
                    return '';
                }
            };

            // Extract relative paths returned from backend (7 images)
            const heroImageRaw = extractImageUrl(heroImageRes);
            const img1Raw = extractImageUrl(image1Res);
            const img2Raw = extractImageUrl(image2Res);
            const img3Raw = extractImageUrl(image3Res);
            const img4Raw = extractImageUrl(image4Res);
            const img5Raw = extractImageUrl(image5Res);
            const img6Raw = extractImageUrl(image6Res);
            const img7Raw = extractImageUrl(image7Res);

            // Debug: Log extracted URLs
            console.log('🔍 Extracted image URLs:', {
                hero: heroImageRaw,
                img1: img1Raw,
                img2: img2Raw,
                img3: img3Raw,
                img4: img4Raw,
                img5: img5Raw,
                img6: img6Raw,
                img7: img7Raw
            });

            console.log('📂 Backend returned these relative paths:', {
                hero: heroImageRaw,
                img1: img1Raw,
                img2: img2Raw,
                img3: img3Raw,
                img4: img4Raw,
                img5: img5Raw,
                img6: img6Raw,
                img7: img7Raw
            });

            // Convert to absolute URLs
            const heroImage = heroImageRaw ? getImageUrl(heroImageRaw) : `https://via.placeholder.com/1792x1024/e2e8f0/64748b.png?text=Hero+Image`;
            const contentImage1 = img1Raw ? getImageUrl(img1Raw) : `https://via.placeholder.com/1792x1024/f1f5f9/475569.png?text=Fundamentals`;
            const contentImage2 = img2Raw ? getImageUrl(img2Raw) : `https://via.placeholder.com/1792x1024/f8fafc/64748b.png?text=Framework`;
            const contentImage3 = img3Raw ? getImageUrl(img3Raw) : `https://via.placeholder.com/1792x1024/f0f4f8/5f6b7a.png?text=Process`;
            const contentImage4 = img4Raw ? getImageUrl(img4Raw) : `https://via.placeholder.com/1792x1024/e8eef3/525f6f.png?text=Advanced+Strategies`;
            const contentImage5 = img5Raw ? getImageUrl(img5Raw) : `https://via.placeholder.com/1792x1024/e5e9f0/404b5f.png?text=Case+Study`;
            const contentImage6 = img6Raw ? getImageUrl(img6Raw) : `https://via.placeholder.com/1792x1024/d7dfe8/3a4555.png?text=Tools`;
            const contentImage7 = img7Raw ? getImageUrl(img7Raw) : `https://via.placeholder.com/1792x1024/cad3e0/2f3948.png?text=Future+Trends`;

            console.log('🌐 Final absolute URLs that will be used in HTML:', {
                hero: heroImage,
                img1: contentImage1,
                img2: contentImage2,
                img3: contentImage3,
                img4: contentImage4,
                img5: contentImage5,
                img6: contentImage6,
                img7: contentImage7
            });

            // Calculate read time from content
            const wordCount = generatedContent.split(/\s+/).length;
            const readTime = Math.max(5, Math.ceil(wordCount / 200));

            // Add reading time header and metadata
            const readingTimeHeader = `
<div style="border-bottom: 1px solid #e5e7eb; padding-bottom: 16px; margin-bottom: 40px;">
  <p style="margin: 0; color: #64748b; font-size: 13px; letter-spacing: 0.5px; text-transform: uppercase; font-weight: 500;">
    ${readTime} min read • ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
  </p>
</div>
`;

            // Insert all 7 high-quality images into content at strategic positions
            console.log('🖼️ Inserting 7 images into content...');
            console.log('Hero image URL being inserted:', heroImage);

            // Use regex to replace image markers even if AI adds extra text like [IMAGE_1: description]
            generatedContent = generatedContent
                .replace(/\[HERO_IMAGE[^\]]*\]/gi, `<figure style="margin: 48px 0;">
  <img src="${heroImage}" alt="Professional illustration for ${mainConcept}" style="width: 100%; max-width: 100%; height: auto; border-radius: 4px; display: block; box-shadow: 0 4px 6px rgba(0,0,0,0.07);" onerror="console.error('Failed to load hero image:', this.src);">
  <figcaption style="text-align: center; color: #94a3b8; font-size: 13px; margin-top: 12px; font-style: italic; font-weight: 300;">
    High-quality AI-generated hero image
  </figcaption>
</figure>`)
                .replace(/\[IMAGE_1[^\]]*\]/gi, `<figure style="margin: 56px 0;">
  <img src="${contentImage1}" alt="Educational visualization of ${mainConcept} fundamentals" style="width: 100%; max-width: 100%; height: auto; border-radius: 2px; display: block; box-shadow: 0 2px 4px rgba(0,0,0,0.06);" onerror="console.error('Failed to load image 1:', this.src);">
  <figcaption style="text-align: center; color: #94a3b8; font-size: 13px; margin-top: 12px; font-style: italic; font-weight: 300;">
    Fundamental concepts illustrated
  </figcaption>
</figure>`)
                .replace(/\[IMAGE_2[^\]]*\]/gi, `<figure style="margin: 56px 0;">
  <img src="${contentImage2}" alt="Strategic framework for ${mainConcept}" style="width: 100%; max-width: 100%; height: auto; border-radius: 2px; display: block; box-shadow: 0 2px 4px rgba(0,0,0,0.06);" onerror="console.error('Failed to load image 2:', this.src);">
  <figcaption style="text-align: center; color: #94a3b8; font-size: 13px; margin-top: 12px; font-style: italic; font-weight: 300;">
    Implementation framework visualization
  </figcaption>
</figure>`)
                .replace(/\[IMAGE_3[^\]]*\]/gi, `<figure style="margin: 56px 0;">
  <img src="${contentImage3}" alt="Process methodology for ${mainConcept}" style="width: 100%; max-width: 100%; height: auto; border-radius: 2px; display: block; box-shadow: 0 2px 4px rgba(0,0,0,0.06);" onerror="console.error('Failed to load image 3:', this.src);">
  <figcaption style="text-align: center; color: #94a3b8; font-size: 13px; margin-top: 12px; font-style: italic; font-weight: 300;">
    Step-by-step process in action
  </figcaption>
</figure>`)
                .replace(/\[IMAGE_4[^\]]*\]/gi, `<figure style="margin: 56px 0;">
  <img src="${contentImage4}" alt="Advanced strategies for ${mainConcept}" style="width: 100%; max-width: 100%; height: auto; border-radius: 2px; display: block; box-shadow: 0 2px 4px rgba(0,0,0,0.06);" onerror="console.error('Failed to load image 4:', this.src);">
  <figcaption style="text-align: center; color: #94a3b8; font-size: 13px; margin-top: 12px; font-style: italic; font-weight: 300;">
    Advanced techniques and best practices
  </figcaption>
</figure>`)
                .replace(/\[IMAGE_5[^\]]*\]/gi, `<figure style="margin: 56px 0;">
  <img src="${contentImage5}" alt="Real-world application of ${mainConcept}" style="width: 100%; max-width: 100%; height: auto; border-radius: 2px; display: block; box-shadow: 0 2px 4px rgba(0,0,0,0.06);" onerror="console.error('Failed to load image 5:', this.src);">
  <figcaption style="text-align: center; color: #94a3b8; font-size: 13px; margin-top: 12px; font-style: italic; font-weight: 300;">
    Professional case study example
  </figcaption>
</figure>`)
                .replace(/\[IMAGE_6[^\]]*\]/gi, `<figure style="margin: 56px 0;">
  <img src="${contentImage6}" alt="Tools and resources for ${mainConcept}" style="width: 100%; max-width: 100%; height: auto; border-radius: 2px; display: block; box-shadow: 0 2px 4px rgba(0,0,0,0.06);" onerror="console.error('Failed to load image 6:', this.src);">
  <figcaption style="text-align: center; color: #94a3b8; font-size: 13px; margin-top: 12px; font-style: italic; font-weight: 300;">
    Recommended tools and resources
  </figcaption>
</figure>`)
                .replace(/\[IMAGE_7[^\]]*\]/gi, `<figure style="margin: 56px 0;">
  <img src="${contentImage7}" alt="Future insights for ${mainConcept}" style="width: 100%; max-width: 100%; height: auto; border-radius: 2px; display: block; box-shadow: 0 2px 4px rgba(0,0,0,0.06);" onerror="console.error('Failed to load image 7:', this.src);">
  <figcaption style="text-align: center; color: #94a3b8; font-size: 13px; margin-top: 12px; font-style: italic; font-weight: 300;">
    Future trends and forward-thinking perspective
  </figcaption>
</figure>`);

            // Prepend reading time header
            generatedContent = readingTimeHeader + generatedContent;

            // Log final content to verify images are present
            const imageCount = (generatedContent.match(/<img/g) || []).length;
            console.log(`✅ Final content has ${imageCount} <img> tags`);
            console.log('Sample of generated content:', generatedContent.substring(0, 500));

            // Step 4: Generate excerpt
            const excerptResponse = await blogAPI.generateExcerpt({ content: generatedContent, skipUsageTracking: true });
            const generatedExcerpt = excerptResponse.data.excerpt || `Discover comprehensive strategies and expert insights for ${aiPrompt.slice(0, 70)}.`;

            // Step 5: Generate SEO metadata
            const seoResponse = await blogAPI.generateSEO({
                title: generatedTitle,
                content: generatedContent,
                skipUsageTracking: true
            });
            const seoData = seoResponse.data;

            const keywords = seoData.keywords || aiPrompt.toLowerCase().split(' ').filter(w => w.length > 3).slice(0, 5);
            const generatedTags = Array.isArray(keywords) ? keywords.slice(0, 8) : [
                ...aiPrompt.toLowerCase().split(' ').filter(w => w.length > 3).slice(0, 4),
                'strategy',
                'guide',
                'best-practices',
                new Date().getFullYear().toString()
            ].slice(0, 8);

            // Store preview for user review
            const preview = {
                title: generatedTitle,
                content: generatedContent,
                excerpt: generatedExcerpt,
                tags: generatedTags,
                keywords: keywords,
                readTime: readTime,
                imagePrompts: {
                    hero: heroImagePrompt,
                    img1: image1Prompt,
                    img2: image2Prompt,
                    img3: image3Prompt,
                    img4: image4Prompt,
                    img5: image5Prompt,
                    img6: image6Prompt,
                    img7: image7Prompt
                }
            };

            console.log('📝 Setting generated post preview with', imageCount, 'images');
            console.log('Preview content length:', generatedContent.length, 'characters');

            setGeneratedPostPreview(preview);

            // 🎯 RECORD QUOTA IMMEDIATELY AFTER SUCCESSFUL GENERATION
            // Quota is reduced as soon as post is generated, regardless of whether user accepts it
            // NOTE: Only post quota is recorded here. Images are free with post generation.
            // Image quota is only used when user manually regenerates individual images.
            console.log('💰 Recording quota usage immediately after generation...');
            try {
                // Record post quota ONLY (images are included in the post generation)
                console.log('📊 Recording 1 AI post quota...');
                await blogAPI.recordAIUsage({
                    action: 'post',
                    data: {
                        type: 'ai-generated-post',
                        includesImages: imageCount,
                        generatedAt: new Date().toISOString()
                    }
                });
                console.log('✅ AI post quota recorded');

                console.log(`✅ QUOTA REDUCED: 1 post (includes ${imageCount} images)`);

            } catch (quotaError) {
                console.error('❌ Error recording quota:', quotaError);

                // If quota recording fails, we need to handle it gracefully
                const errorMessage = quotaError?.response?.data?.message || quotaError?.message || 'Unknown error';
                console.error('❌ Quota recording failed:', errorMessage);

                // Show error but don't block the preview (post was already generated)
                setTimeout(() => {
                    alert(`⚠️ Warning: Post generated but quota recording failed.\n\n${errorMessage}\n\nPlease contact support. The post is shown below but quota may not have been properly recorded.`);
                }, 500);
            }

        } catch (error) {
            console.error('Error generating content:', error);
            alert('Failed to generate content. Please try again.');
        } finally {
            setGeneratingAI(false);
        }
    };

    // Analyze Current Post (for the main form, not generated post)
    const handleAnalyzeCurrentPost = async () => {
        // Validate required fields
        if (!formData.title || !formData.content) {
            alert('Please add a title and content before analyzing');
            return;
        }

        setAnalyzingComprehensive(true);
        try {
            console.log('🔍 Starting comprehensive analysis of current post...');

            const response = await blogAPI.analyzePost({
                title: formData.title,
                content: formData.content,
                excerpt: formData.excerpt,
                slug: formData.slug || formData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
                tags: formData.tags,
                seo: {
                    metaTitle: formData.seo.metaTitle || formData.title,
                    metaDescription: formData.seo.metaDescription || formData.excerpt,
                    keywords: formData.seo.keywords || []
                }
            });

            if (response.data && response.data.analysis) {
                setComprehensiveAnalysis(response.data.analysis);
                console.log('✅ Analysis complete:', response.data.analysis);

                // Scroll to analysis section after it loads
                setTimeout(() => {
                    const analysisElement = document.querySelector('[data-comprehensive-analysis]');
                    if (analysisElement) {
                        analysisElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }, 100);
            }
        } catch (error) {
            console.error('Error analyzing post:', error);
            const errorMessage = error?.response?.data?.message || error.message || 'Failed to analyze post';

            // Check if it's a quota exceeded error
            if (error?.response?.data?.requiresUpgrade) {
                setSubscriptionMessage(errorMessage);
                setQuotaExceededType('analysis');
                setShowSubscriptionModal(true);
            } else {
                alert('Failed to analyze post: ' + errorMessage);
            }
        } finally {
            setAnalyzingComprehensive(false);
        }
    };

    // Accept Generated Post
    // Analyze Generated Post with Comprehensive AI Analysis
    const handleAnalyzeGeneratedPost = async (preview) => {
        setAnalyzingGeneratedPost(true);
        try {
            console.log('🔍 Starting comprehensive analysis of generated post...');

            const response = await blogAPI.analyzePost({
                title: preview.title,
                content: preview.content,
                excerpt: preview.excerpt,
                slug: preview.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
                tags: preview.tags,
                seo: {
                    metaTitle: preview.title,
                    metaDescription: preview.excerpt,
                    keywords: preview.keywords || []
                }
            });

            if (response.data && response.data.analysis) {
                setGeneratedPostAnalysis(response.data.analysis);
                console.log('✅ Analysis complete:', response.data.analysis);

                // Scroll to analysis section after it loads
                setTimeout(() => {
                    const analysisElement = document.querySelector('[data-analysis-section]');
                    if (analysisElement) {
                        analysisElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }, 100);
            }
        } catch (error) {
            console.error('Error analyzing post:', error);
            const errorMessage = error?.response?.data?.message || error.message || 'Failed to analyze post';

            // Check if it's a quota exceeded error
            if (error?.response?.data?.requiresUpgrade) {
                setSubscriptionMessage(errorMessage);
                setQuotaExceededType('analysis');
                setShowSubscriptionModal(true);
            } else {
                alert('Failed to analyze post: ' + errorMessage);
            }
        } finally {
            setAnalyzingGeneratedPost(false);
        }
    };

    // Accept AI Generated Post
    const handleAcceptAIPost = async () => {
        if (generatedPostPreview) {
            console.log('✅ Accepting AI-generated post (quota already reduced during generation)');

            // Save to last accepted post before clearing
            setLastAcceptedPost({
                ...generatedPostPreview,
                acceptedAt: new Date().toISOString()
            });

            // Move generated content to the editor
            setFormData(prev => ({
                ...prev,
                title: generatedPostPreview.title,
                content: generatedPostPreview.content,
                excerpt: generatedPostPreview.excerpt,
                tags: [...new Set([...prev.tags, ...generatedPostPreview.tags])],
                seo: {
                    ...prev.seo,
                    metaTitle: generatedPostPreview.title,
                    metaDescription: generatedPostPreview.excerpt,
                    keywords: [...new Set([...prev.seo.keywords, ...generatedPostPreview.keywords])]
                }
            }));

            // Manually update the Tiptap editor content
            if (editor && generatedPostPreview.content) {
                editor.commands.setContent(generatedPostPreview.content);
            }

            // Clean up and close modal
            setIsAIGenerated(false);
            setShowAIModal(false);
            setGeneratedPostPreview(null);
            setSeoAnalysisModal(null);
            setAIPrompt('');
        }
    };

    // Regenerate with same prompt
    const handleRegenerate = async () => {
        // Clear previous results
        setGeneratedPostPreview(null);
        setGeneratedPostAnalysis(null);
        setSeoAnalysisModal(null);
        setMetricImprovements({});

        // Check quota again before regenerating
        const canGenerate = await checkQuota('post');
        if (!canGenerate) {
            return; // Modal will be shown automatically by checkQuota
        }

        // Regenerate with the same prompt
        handleAIGeneratePost();
    };

    // Improve Content Based on Analysis
    const handleImproveContent = async () => {
        if (!generatedPostPreview || !generatedPostAnalysis) {
            return;
        }

        setImprovingContent(true);
        try {
            console.log('🔧 Improving content based on analysis recommendations...');

            // Gather all recommendations
            const allRecommendations = [
                ...(generatedPostAnalysis.recommendations?.critical || []),
                ...(generatedPostAnalysis.recommendations?.high || []),
                ...(generatedPostAnalysis.recommendations?.medium || [])
            ].slice(0, 5); // Top 5 recommendations

            if (allRecommendations.length === 0) {
                alert('No recommendations available to improve upon.');
                setImprovingContent(false);
                return;
            }

            console.log('📋 Recommendations to address:', allRecommendations);

            // Create a more detailed improvement prompt
            const improvementPrompt = `You are a professional content editor. Your task is to improve the following blog post by specifically addressing each recommendation listed below.

CRITICAL IMPROVEMENTS NEEDED:
${allRecommendations.map((rec, idx) => `${idx + 1}. ${rec}`).join('\n')}

CURRENT TITLE:
${generatedPostPreview.title}

CURRENT CONTENT:
${generatedPostPreview.content}

YOUR TASK:
You MUST make specific improvements to address EACH recommendation above. Here's how:

1. If readability needs improvement:
   - Break long paragraphs into shorter ones (2-4 sentences each)
   - Use bullet points and numbered lists where appropriate
   - Add transition words and vary sentence length
   - Simplify complex sentences

2. If internal links are needed:
   - Add HTML anchor tags like: <a href="/related-article">link text</a>
   - Link to relevant topics within the content
   - Use descriptive anchor text

3. If keywords need to be added:
   - Naturally incorporate the missing keywords throughout
   - Use them in headings and body text
   - Don't force or overstuff - keep it natural

4. If content length is insufficient:
   - Add more detailed explanations
   - Include examples and case studies
   - Expand on key points

CRITICAL RULES:
- Keep ALL existing HTML structure and ALL image markers [HERO_IMAGE], [IMAGE_1], [IMAGE_2], etc.
- Do NOT remove or modify any <figure> or <img> tags
- Return ONLY the improved HTML content
- Do NOT add markdown code blocks or explanations
- Make SUBSTANTIAL improvements - don't just make minor changes

Return the significantly improved content now:`;

            const response = await blogAPI.generateContent({
                prompt: improvementPrompt,
                useRawPrompt: true,
                skipUsageTracking: false
            });

            let improvedContent = response.data.content || '';

            // Remove markdown code blocks if present
            improvedContent = improvedContent
                .replace(/^```html\s*/i, '')
                .replace(/^```\s*/, '')
                .replace(/\s*```\s*$/, '')
                .trim();

            console.log('📝 Original content length:', generatedPostPreview.content.length);
            console.log('📝 Improved content length:', improvedContent.length);

            // Check if content actually changed
            if (improvedContent === generatedPostPreview.content) {
                console.warn('⚠️ Content did not change after improvement');
                alert('The AI could not significantly improve the content. The recommendations might already be addressed, or you may need to try again.');
                setImprovingContent(false);
                return;
            }

            // Update the preview with improved content
            setGeneratedPostPreview({
                ...generatedPostPreview,
                content: improvedContent
            });

            // Clear previous analysis to trigger re-analysis
            setGeneratedPostAnalysis(null);

            console.log('✅ Content improved successfully');

            // Show success message
            alert('✅ Content has been improved! Re-analyzing now...');

            // Automatically re-analyze after 1.5 seconds
            setTimeout(() => {
                handleAnalyzeGeneratedPost({
                    ...generatedPostPreview,
                    content: improvedContent
                });
            }, 1500);

        } catch (error) {
            console.error('❌ Error improving content:', error);
            const errorMessage = error?.response?.data?.message || error.message || 'Unknown error';
            alert(`Failed to improve content: ${errorMessage}\n\nPlease try again.`);
        } finally {
            setImprovingContent(false);
        }
    };

    // Improve Current Form Content
    const handleImproveCurrentContent = async () => {
        if (!formData.content || !comprehensiveAnalysis) {
            return;
        }

        setImprovingContent(true);
        try {
            console.log('🔧 Improving current post content based on analysis recommendations...');

            // Gather all recommendations
            const allRecommendations = [
                ...(comprehensiveAnalysis.recommendations?.critical || []),
                ...(comprehensiveAnalysis.recommendations?.high || []),
                ...(comprehensiveAnalysis.recommendations?.medium || [])
            ].slice(0, 5); // Top 5 recommendations

            if (allRecommendations.length === 0) {
                alert('No recommendations available to improve upon.');
                setImprovingContent(false);
                return;
            }

            console.log('📋 Recommendations to address:', allRecommendations);

            // Create a comprehensive improvement prompt following 2025 best practices
            const currentYear = new Date().getFullYear();
            const improvementPrompt = `You are an expert content strategist and SEO specialist. Your task is to significantly improve this blog post to achieve a quality score of 90+ by addressing specific recommendations.

CURRENT POST DETAILS:
Title: "${formData.title}"
Excerpt: "${formData.excerpt || 'Not provided'}"
Content Length: ${formData.content.length} characters

CURRENT ANALYSIS SCORE: ${comprehensiveAnalysis.summary?.overallScore || 0}/100

CRITICAL IMPROVEMENTS REQUIRED:
${allRecommendations.map((rec, idx) => `${idx + 1}. ${rec}`).join('\n')}

YOUR IMPROVEMENT TASKS:

1. READABILITY ENHANCEMENT (Target: Flesch Score 60-70):
   - Break paragraphs into 2-4 sentence chunks
   - Use transition words (however, furthermore, additionally, therefore)
   - Vary sentence length (mix short punchy sentences with longer explanatory ones)
   - Replace complex words with simpler alternatives
   - Add bullet points and numbered lists for scanability
   - Use subheadings (H2, H3) every 300-400 words

2. SEO OPTIMIZATION (Target: High ranking potential):
   - Naturally incorporate focus keywords: ${formData.seo?.keywords?.join(', ') || 'identify from content'}
   - Add LSI keywords (semantically related terms)
   - Include keywords in:
     * First paragraph (naturally)
     * At least 2 subheadings
     * Throughout body (1-2% density)
     * Final paragraph
   - Add long-tail keyword variations
   - Include question-based phrases people search for

3. INTERNAL LINKING (Target: 3-5 relevant links):
   - Add contextual internal links like: <a href="/related-topic">anchor text</a>
   - Use descriptive anchor text (not "click here")
   - Link to related concepts mentioned in the content
   - Examples: 
     * If mentioning "SEO basics" → link to /seo-guide
     * If mentioning specific features → link to relevant pages

4. CONTENT DEPTH & VALUE (Target: 1500+ words):
   - Expand thin sections with:
     * Real-world examples
     * Step-by-step instructions
     * Expert tips and best practices
     * Common mistakes to avoid
     * Pro tips and insider knowledge
   - Add data points or statistics (use realistic ranges)
   - Include actionable takeaways
   - Address user questions and concerns

5. STRUCTURE OPTIMIZATION:
   - Strong hook in first paragraph (grab attention immediately)
   - Clear section headers that promise value
   - Use of lists and tables where appropriate
   - Compelling conclusion with clear call-to-action
   - Add "Key Takeaways" or "Summary" section

6. ENGAGEMENT ELEMENTS:
   - Add questions to readers
   - Include actionable tips in boxes
   - Use power words (proven, essential, ultimate, complete, expert)
   - Create urgency where appropriate (${currentYear} updates, latest trends)

CRITICAL FORMATTING RULES:
- Maintain ALL existing HTML tags and structure
- Keep ALL images and image tags intact
- Use proper heading hierarchy (H2 for main sections, H3 for subsections)
- Return ONLY the improved HTML content
- NO markdown code blocks (no \`\`\`html)
- NO explanatory text - just the improved content

QUALITY TARGETS:
- Readability: 60-70 Flesch score
- Word Count: 1500+ words
- Keyword Density: 1-2%
- Internal Links: 3-5
- Headings: Every 300-400 words
- Paragraph Length: 2-4 sentences

Make SUBSTANTIAL improvements. This should be significantly better than the original. Return the improved HTML content now:`;

            const response = await blogAPI.generateContent({
                prompt: improvementPrompt,
                useRawPrompt: true,
                skipUsageTracking: false,
                tone: 'professional',
                length: 'long'
            });

            let improvedContent = response.data.content || '';

            // Remove markdown code blocks if present
            improvedContent = improvedContent
                .replace(/^```html\s*/i, '')
                .replace(/^```\s*/, '')
                .replace(/\s*```\s*$/, '')
                .trim();

            console.log('📝 Original content length:', formData.content.length);
            console.log('📝 Improved content length:', improvedContent.length);

            // Check if content actually changed
            if (improvedContent === formData.content || improvedContent.length < formData.content.length * 0.9) {
                console.warn('⚠️ Content did not improve significantly');
                alert('The AI could not significantly improve the content. Try clicking again or the recommendations might already be addressed.');
                setImprovingContent(false);
                return;
            }

            // Update the form data with improved content
            setFormData(prev => ({
                ...prev,
                content: improvedContent
            }));

            // Update the editor
            if (editor) {
                editor.commands.setContent(improvedContent);
            }

            // Clear previous analysis to trigger re-analysis
            setComprehensiveAnalysis(null);

            console.log('✅ Content improved successfully - length increased by', improvedContent.length - formData.content.length, 'characters');

            // Show success message
            alert(`✅ Content has been improved!\n\n• Original: ${formData.content.length} characters\n• Improved: ${improvedContent.length} characters\n• Change: +${improvedContent.length - formData.content.length} characters\n\nRe-analyzing now...`);

            // Automatically re-analyze after 1.5 seconds
            setTimeout(() => {
                handleAnalyzeCurrentPost();
            }, 1500);

        } catch (error) {
            console.error('❌ Error improving content:', error);
            const errorMessage = error?.response?.data?.message || error.message || 'Unknown error';
            alert(`Failed to improve content: ${errorMessage}\n\nPlease try again.`);
        } finally {
            setImprovingContent(false);
        }
    };

    // Accept Generated Post (original function - keep for backwards compatibility)
    const handleAcceptGenerated = () => {
        if (generatedPostPreview) {
            console.log('📝 Accepting generated post:', generatedPostPreview);

            // Update form data
            const newFormData = {
                ...formData,
                title: generatedPostPreview.title || formData.title,
                content: generatedPostPreview.content || formData.content,
                excerpt: generatedPostPreview.excerpt || formData.excerpt,
                tags: generatedPostPreview.tags || formData.tags,
                seo: {
                    ...formData.seo,
                    metaTitle: generatedPostPreview.seoTitle || formData.seo.metaTitle,
                    metaDescription: generatedPostPreview.seoDescription || formData.seo.metaDescription,
                    keywords: generatedPostPreview.keywords || formData.seo.keywords
                }
            };

            setFormData(newFormData);

            // Manually update the Tiptap editor content
            if (editor && generatedPostPreview.content) {
                editor.commands.setContent(generatedPostPreview.content);
            }

            setGeneratedPostPreview(null);
            setShowAIModal(false);
            setAIPrompt('');

            console.log('✅ Form updated with generated content');
        }
    };

    // AI Generate Content
    const handleGenerateContent = async () => {
        if (!formData.title) {
            alert('Please enter a title first');
            return;
        }

        try {
            setGeneratingContent(true);

            // Check subscription quota
            const quotaCheck = await subscriptionService.canPerformAIAction('call');
            if (!quotaCheck.allowed || quotaCheck.success === false) {
                alert(quotaCheck.reason || quotaCheck.message || 'No active subscription found. Please subscribe to use AI features.');
                setGeneratingContent(false);
                return;
            }

            const response = await blogAPI.generateContent({
                prompt: `Write a comprehensive blog post about: ${formData.title}`,
                tone: 'professional',
                length: 'long'
            });
            if (response.data.content) {
                setFormData(prev => ({
                    ...prev,
                    content: response.data.content
                }));
            }
        } catch (error) {
            console.error('Error generating content:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to generate content';
            alert(errorMessage);
        } finally {
            setGeneratingContent(false);
        }
    };

    // AI Generate Excerpt
    const handleGenerateExcerpt = async () => {
        if (!formData.content) {
            alert('Please write some content first');
            return;
        }

        try {
            setGeneratingExcerpt(true);

            // Check subscription quota
            const quotaCheck = await subscriptionService.canPerformAIAction('call');
            if (!quotaCheck.allowed || quotaCheck.success === false) {
                alert(quotaCheck.reason || quotaCheck.message || 'No active subscription found. Please subscribe to use AI features.');
                setGeneratingExcerpt(false);
                return;
            }

            // Call the specific excerpt generation endpoint
            const response = await blogAPI.generateExcerpt({
                content: formData.content,
                maxLength: 150
            });
            if (response.data.excerpt) {
                setFormData(prev => ({
                    ...prev,
                    excerpt: response.data.excerpt
                }));
            }
        } catch (error) {
            console.error('Error generating excerpt:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to generate excerpt';
            alert(errorMessage);
        } finally {
            setGeneratingExcerpt(false);
        }
    };

    // AI Generate Tags
    const handleGenerateTags = async () => {
        if (!formData.content && !formData.title) {
            alert('Please write some content or title first');
            return;
        }

        try {
            setGeneratingTags(true);

            // Check subscription quota
            const quotaCheck = await subscriptionService.canPerformAIAction('call');
            if (!quotaCheck.allowed || quotaCheck.success === false) {
                alert(quotaCheck.reason || quotaCheck.message || 'No active subscription found. Please subscribe to use AI features.');
                setGeneratingTags(false);
                return;
            }

            // Call the specific tags generation endpoint
            const response = await blogAPI.generateTags({
                title: formData.title,
                content: formData.content,
                count: 12 // Generate 10-12 tags
            });

            if (response.data.tags && Array.isArray(response.data.tags)) {
                setFormData(prev => ({
                    ...prev,
                    tags: [...new Set([...prev.tags, ...response.data.tags])]
                }));
            }
        } catch (error) {
            console.error('Error generating tags:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to generate tags';
            alert(errorMessage);
        } finally {
            setGeneratingTags(false);
        }
    };

    // AI Generate SEO
    const handleGenerateSEO = async () => {
        if (!formData.title || !formData.content) {
            alert('Please write title and content first');
            return;
        }

        try {
            setGeneratingSEOTitle(true);

            // Check subscription quota
            const quotaCheck = await subscriptionService.canPerformAIAction('call');
            if (!quotaCheck.allowed || quotaCheck.success === false) {
                alert(quotaCheck.reason || quotaCheck.message || 'No active subscription found. Please subscribe to use AI features.');
                setGeneratingSEOTitle(false);
                return;
            }

            const response = await blogAPI.generateSEO({
                title: formData.title,
                content: formData.content,
                keywords: formData.seo.keywords
            });

            console.log('SEO Response:', response.data);

            if (response.data && (response.data.metaTitle || response.data.metaDescription)) {
                setFormData(prev => ({
                    ...prev,
                    seo: {
                        ...prev.seo,
                        metaTitle: response.data.metaTitle || prev.seo.metaTitle,
                        metaDescription: response.data.metaDescription || prev.seo.metaDescription,
                        keywords: response.data.secondaryKeywords || response.data.keywords || prev.seo.keywords
                    }
                }));
            }
        } catch (error) {
            console.error('Error generating SEO:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to generate SEO';
            alert(errorMessage);
        } finally {
            setGeneratingSEOTitle(false);
        }
    };

    // Inline Category Creation
    const handleCreateInlineCategory = async () => {
        if (!inlineCategoryName.trim()) {
            alert('Please enter a category name');
            return;
        }

        try {
            setCreatingInlineCategory(true);

            // Auto-generate slug from name
            const slug = inlineCategoryName.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');

            const categoryData = {
                name: inlineCategoryName,
                slug,
                description: `${inlineCategoryName} category`
            };

            const response = await blogAPI.createCategory(categoryData);

            // Automatically select the newly created category
            if (response.data && response.data._id) {
                setFormData(prev => ({
                    ...prev,
                    category: response.data._id
                }));

                // Call onCategoryCreated callback if provided (to refresh categories list)
                if (onCategoryCreated) {
                    onCategoryCreated(response.data);
                }
            }

            // Reset form and hide
            setInlineCategoryName('');
            setShowInlineCategoryForm(false);

        } catch (error) {
            console.error('Error creating category:', error);
            alert(error.response?.data?.message || 'Failed to create category');
        } finally {
            setCreatingInlineCategory(false);
        }
    };

    const wordCount = formData.content.replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean).length;
    const excerptCharCount = formData.excerpt.length;
    const seoTitleCharCount = formData.seo.metaTitle.length;
    const seoDescCharCount = formData.seo.metaDescription.length;

    return (
        <div className="blog-editor-overlay">
            <div className="blog-editor-modal">
                <div className="blog-editor-header">
                    <div>
                        <h1>{post ? 'Edit Post' : 'Create New Post'}</h1>
                        <p className="editor-subtitle">Fill in the details below to create your blog post</p>
                    </div>
                    <div className="header-actions">
                        <button
                            className="ai-generate-post-btn"
                            onClick={() => setShowAIModal(true)}
                        >
                            <Sparkles size={20} />
                            AI Generate Post
                        </button>
                        <button className="close-btn" onClick={onClose}>
                            <X size={24} />
                        </button>
                    </div>
                </div>

                <div className="blog-editor-content">
                    {/* Title */}
                    <div className="form-section">
                        <input
                            type="text"
                            placeholder="Title"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            className="title-input"
                        />
                    </div>

                    {/* Content Editor */}
                    <div className="form-section">
                        <div className="section-header">
                            <label>Content *</label>
                            <button
                                className="ai-generate-btn"
                                onClick={handleGenerateContent}
                                disabled={generatingContent}
                            >
                                {generatingContent ? (
                                    <><Loader2 size={16} className="animate-spin" /> Generating...</>
                                ) : (
                                    <><Sparkles size={16} /> AI Generate</>
                                )}
                            </button>
                        </div>

                        {/* Rich Text Toolbar */}
                        {editor && (
                            <div className="editor-toolbar">
                                {/* Text Formatting */}
                                <div className="toolbar-group">
                                    <button
                                        type="button"
                                        onClick={() => editor.chain().focus().toggleBold().run()}
                                        className={`toolbar-btn ${editor.isActive('bold') ? 'bg-gray-300' : ''}`}
                                        title="Bold (Ctrl+B)"
                                    >
                                        <Bold size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => editor.chain().focus().toggleItalic().run()}
                                        className={`toolbar-btn ${editor.isActive('italic') ? 'bg-gray-300' : ''}`}
                                        title="Italic (Ctrl+I)"
                                    >
                                        <Italic size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                                        className={`toolbar-btn ${editor.isActive('underline') ? 'bg-gray-300' : ''}`}
                                        title="Underline (Ctrl+U)"
                                    >
                                        <UnderlineIcon size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => editor.chain().focus().toggleStrike().run()}
                                        className={`toolbar-btn ${editor.isActive('strike') ? 'bg-gray-300' : ''}`}
                                        title="Strikethrough"
                                    >
                                        <Strikethrough size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => editor.chain().focus().toggleCode().run()}
                                        className={`toolbar-btn ${editor.isActive('code') ? 'bg-gray-300' : ''}`}
                                        title="Code"
                                    >
                                        <Code size={18} />
                                    </button>
                                </div>

                                <div className="toolbar-divider"></div>

                                {/* Headings */}
                                <div className="toolbar-group">
                                    <button
                                        type="button"
                                        onClick={() => editor.chain().focus().setParagraph().run()}
                                        className={`toolbar-btn ${editor.isActive('paragraph') ? 'bg-gray-300' : ''}`}
                                        title="Paragraph"
                                    >
                                        <Type size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                                        className={`toolbar-btn ${editor.isActive('heading', { level: 1 }) ? 'bg-gray-300' : ''}`}
                                        title="Heading 1"
                                    >
                                        <Heading1 size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                                        className={`toolbar-btn ${editor.isActive('heading', { level: 2 }) ? 'bg-gray-300' : ''}`}
                                        title="Heading 2"
                                    >
                                        <Heading2 size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                                        className={`toolbar-btn ${editor.isActive('heading', { level: 3 }) ? 'bg-gray-300' : ''}`}
                                        title="Heading 3"
                                    >
                                        <Heading3 size={18} />
                                    </button>
                                </div>

                                <div className="toolbar-divider"></div>

                                {/* Lists & Quotes */}
                                <div className="toolbar-group">
                                    <button
                                        type="button"
                                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                                        className={`toolbar-btn ${editor.isActive('bulletList') ? 'bg-gray-300' : ''}`}
                                        title="Bullet List"
                                    >
                                        <List size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                                        className={`toolbar-btn ${editor.isActive('orderedList') ? 'bg-gray-300' : ''}`}
                                        title="Numbered List"
                                    >
                                        <ListOrdered size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => editor.chain().focus().toggleBlockquote().run()}
                                        className={`toolbar-btn ${editor.isActive('blockquote') ? 'bg-gray-300' : ''}`}
                                        title="Quote"
                                    >
                                        <Quote size={18} />
                                    </button>
                                </div>

                                <div className="toolbar-divider"></div>

                                {/* Alignment */}
                                <div className="toolbar-group">
                                    <button
                                        type="button"
                                        onClick={() => editor.chain().focus().setTextAlign('left').run()}
                                        className={`toolbar-btn ${editor.isActive({ textAlign: 'left' }) ? 'bg-gray-300' : ''}`}
                                        title="Align Left"
                                    >
                                        <AlignLeft size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => editor.chain().focus().setTextAlign('center').run()}
                                        className={`toolbar-btn ${editor.isActive({ textAlign: 'center' }) ? 'bg-gray-300' : ''}`}
                                        title="Align Center"
                                    >
                                        <AlignCenter size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => editor.chain().focus().setTextAlign('right').run()}
                                        className={`toolbar-btn ${editor.isActive({ textAlign: 'right' }) ? 'bg-gray-300' : ''}`}
                                        title="Align Right"
                                    >
                                        <AlignRight size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                                        className={`toolbar-btn ${editor.isActive({ textAlign: 'justify' }) ? 'bg-gray-300' : ''}`}
                                        title="Justify"
                                    >
                                        <AlignJustify size={18} />
                                    </button>
                                </div>

                                <div className="toolbar-divider"></div>

                                {/* Colors */}
                                <div className="toolbar-group">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowColorPicker(!showColorPicker);
                                            setShowHighlightPicker(false);
                                        }}
                                        className="toolbar-btn"
                                        title="Text Color"
                                    >
                                        <Paintbrush size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowHighlightPicker(!showHighlightPicker);
                                            setShowColorPicker(false);
                                        }}
                                        className={`toolbar-btn ${editor.isActive('highlight') ? 'bg-gray-300' : ''}`}
                                        title="Highlight"
                                    >
                                        <Highlighter size={18} />
                                    </button>
                                </div>

                                <div className="toolbar-divider"></div>

                                {/* Link & Image */}
                                <div className="toolbar-group">
                                    <button
                                        type="button"
                                        onClick={() => setShowLinkInput(!showLinkInput)}
                                        className={`toolbar-btn ${editor.isActive('link') ? 'bg-gray-300' : ''}`}
                                        title="Add Link"
                                    >
                                        <LinkIcon size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowImageInput(!showImageInput)}
                                        className="toolbar-btn"
                                        title="Add Image"
                                    >
                                        <ImageIcon size={18} />
                                    </button>
                                </div>

                                <div className="toolbar-divider"></div>

                                {/* Undo/Redo */}
                                <div className="toolbar-group">
                                    <button
                                        type="button"
                                        onClick={() => editor.chain().focus().undo().run()}
                                        disabled={!editor.can().undo()}
                                        className="toolbar-btn disabled:opacity-50"
                                        title="Undo (Ctrl+Z)"
                                    >
                                        <Undo size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => editor.chain().focus().redo().run()}
                                        disabled={!editor.can().redo()}
                                        className="toolbar-btn disabled:opacity-50"
                                        title="Redo (Ctrl+Y)"
                                    >
                                        <Redo size={18} />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Color Picker */}
                        {showColorPicker && (
                            <div className="absolute z-10 bg-white border border-gray-300 rounded-lg shadow-lg p-3 mt-1">
                                <div className="text-xs font-semibold text-gray-700 mb-2">Text Color</div>
                                <div className="grid grid-cols-7 gap-1">
                                    {colors.map((color) => (
                                        <button
                                            key={color}
                                            type="button"
                                            onClick={() => setColor(color)}
                                            className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
                                            style={{ backgroundColor: color }}
                                            title={color}
                                        />
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        editor?.chain().focus().unsetColor().run();
                                        setShowColorPicker(false);
                                    }}
                                    className="mt-2 w-full text-xs bg-gray-100 hover:bg-gray-200 py-1 px-2 rounded"
                                >
                                    Remove Color
                                </button>
                            </div>
                        )}

                        {/* Highlight Color Picker */}
                        {showHighlightPicker && (
                            <div className="absolute z-10 bg-white border border-gray-300 rounded-lg shadow-lg p-3 mt-1">
                                <div className="text-xs font-semibold text-gray-700 mb-2">Highlight Color</div>
                                <div className="grid grid-cols-7 gap-1">
                                    {colors.map((color) => (
                                        <button
                                            key={color}
                                            type="button"
                                            onClick={() => setHighlight(color)}
                                            className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
                                            style={{ backgroundColor: color }}
                                            title={color}
                                        />
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        editor?.chain().focus().unsetHighlight().run();
                                        setShowHighlightPicker(false);
                                    }}
                                    className="mt-2 w-full text-xs bg-gray-100 hover:bg-gray-200 py-1 px-2 rounded"
                                >
                                    Remove Highlight
                                </button>
                            </div>
                        )}

                        {/* Link Input */}
                        {showLinkInput && (
                            <div className="bg-blue-50 border-b border-gray-300 p-3 flex gap-2">
                                <input
                                    type="url"
                                    value={linkUrl}
                                    onChange={(e) => setLinkUrl(e.target.value)}
                                    placeholder="Enter URL (e.g., https://example.com)"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            addLink();
                                        }
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={addLink}
                                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                                >
                                    Add Link
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        editor?.chain().focus().unsetLink().run();
                                        setShowLinkInput(false);
                                    }}
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                                >
                                    Remove Link
                                </button>
                            </div>
                        )}

                        {/* Image Input */}
                        {showImageInput && (
                            <div className="bg-gray-50 border-b border-gray-300 p-3 flex gap-2">
                                <input
                                    type="url"
                                    value={imageUrl}
                                    onChange={(e) => setImageUrl(e.target.value)}
                                    placeholder="Enter image URL"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            addImage();
                                        }
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={addImage}
                                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                                >
                                    Add Image
                                </button>
                            </div>
                        )}

                        {/* Editor Content */}
                        {editor && (
                            <div className="bg-white min-h-[400px] max-h-[600px] overflow-y-auto border-2 border-gray-300 rounded-b-lg">
                                <EditorContent editor={editor} />
                            </div>
                        )}

                        <div className="char-count">Word count: {wordCount} (optimal: 1000+)</div>

                        {/* Editor Styles */}
                        <style>{`
                            .ProseMirror {
                                outline: none;
                                padding: 1rem;
                                min-height: 400px;
                            }
                            .ProseMirror p.is-editor-empty:first-child::before {
                                content: attr(data-placeholder);
                                float: left;
                                color: #adb5bd;
                                pointer-events: none;
                                height: 0;
                            }
                            .ProseMirror img {
                                max-width: 100%;
                                height: auto;
                                border-radius: 4px;
                            }
                            .ProseMirror blockquote {
                                border-left: 3px solid #e5e7eb;
                                padding-left: 1rem;
                                margin-left: 0;
                                font-style: italic;
                            }
                            .ProseMirror code {
                                background-color: #f3f4f6;
                                border-radius: 0.25rem;
                                padding: 0.125rem 0.25rem;
                                font-family: 'Courier New', monospace;
                            }
                            .ProseMirror a {
                                color: #2563eb;
                                text-decoration: underline;
                                cursor: pointer;
                            }
                            .ProseMirror a:hover {
                                color: #1d4ed8;
                            }
                        `}</style>
                    </div>

                    {/* Excerpt */}
                    <div className="form-section">
                        <div className="section-header">
                            <label>Excerpt</label>
                            <button
                                className="ai-generate-btn"
                                onClick={handleGenerateExcerpt}
                                disabled={generatingExcerpt}
                            >
                                {generatingExcerpt ? (
                                    <><Loader2 size={16} className="animate-spin" /> Generating...</>
                                ) : (
                                    <><Sparkles size={16} /> AI Generate</>
                                )}
                            </button>
                        </div>
                        <textarea
                            value={formData.excerpt}
                            onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                            placeholder="Brief summary that will appear in post previews..."
                            className="excerpt-textarea"
                            rows={3}
                        />
                        <div className="char-count">{excerptCharCount}/160 characters</div>
                    </div>

                    {/* Category and Status */}
                    <div className="form-row">
                        <div className="form-group">
                            <div className="section-header">
                                <label>Category</label>
                                {!showInlineCategoryForm && (
                                    <button
                                        className="add-new-btn"
                                        onClick={() => setShowInlineCategoryForm(true)}
                                        type="button"
                                    >
                                        <Plus size={16} />
                                        New
                                    </button>
                                )}
                            </div>
                            {showInlineCategoryForm ? (
                                <div className="new-category-input">
                                    <input
                                        type="text"
                                        value={inlineCategoryName}
                                        onChange={(e) => setInlineCategoryName(e.target.value)}
                                        placeholder="Enter category name..."
                                        className="form-input"
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleCreateInlineCategory();
                                            }
                                        }}
                                        autoFocus
                                    />
                                    <button
                                        onClick={handleCreateInlineCategory}
                                        className="create-btn"
                                        disabled={creatingInlineCategory || !inlineCategoryName.trim()}
                                    >
                                        {creatingInlineCategory ? (
                                            <Loader2 size={16} className="animate-spin" />
                                        ) : (
                                            <CheckCircle size={16} />
                                        )}
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowInlineCategoryForm(false);
                                            setInlineCategoryName('');
                                        }}
                                        className="cancel-btn"
                                        type="button"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            ) : (
                                <select
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                    className="form-select"
                                >
                                    <option value="">Select category...</option>
                                    {categories.map(cat => (
                                        <option key={cat._id} value={cat._id}>{cat.name}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                        <div className="form-group">
                            <label>Status</label>
                            <select
                                value={formData.status}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                className="form-select"
                            >
                                <option value="draft">Draft</option>
                                <option value="published">Published</option>
                                <option value="scheduled">Scheduled</option>
                                <option value="archived">Archived</option>
                            </select>
                        </div>
                    </div>

                    {/* Tags */}
                    <div className="form-section">
                        <div className="section-header">
                            <label>Tags</label>
                            <button
                                className="ai-generate-btn"
                                onClick={handleGenerateTags}
                                disabled={generatingTags}
                            >
                                {generatingTags ? (
                                    <><Loader2 size={16} className="animate-spin" /> Generating...</>
                                ) : (
                                    <><Sparkles size={16} /> AI Generate</>
                                )}
                            </button>
                        </div>
                        <div className="tags-input-container">
                            <input
                                type="text"
                                value={newTag}
                                onChange={(e) => setNewTag(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                                placeholder="Add Tag"
                                className="tag-input"
                            />
                            <button onClick={handleAddTag} className="add-tag-icon">
                                <Plus size={20} />
                            </button>
                        </div>
                        {formData.tags.length === 0 ? (
                            <div className="no-tags-message">
                                No tags added yet. Click "AI Generate" or add manually.
                            </div>
                        ) : (
                            <div className="tags-list">
                                {formData.tags.map((tag, index) => (
                                    <span key={index} className="tag-item">
                                        {tag}
                                        <button onClick={() => handleRemoveTag(tag)} className="tag-remove">×</button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Featured Image */}
                    <div className="form-section">
                        <label>Featured Image</label>
                        <div className="image-tabs">
                            <button
                                className={`image-tab ${featuredImageTab === 'upload' ? 'active' : ''}`}
                                onClick={() => setFeaturedImageTab('upload')}
                            >
                                <Upload size={16} />
                                Upload
                            </button>
                            <button
                                className={`image-tab ${featuredImageTab === 'library' ? 'active' : ''}`}
                                onClick={() => setFeaturedImageTab('library')}
                            >
                                <ImageIcon size={16} />
                                Library
                            </button>
                            <button
                                className={`image-tab ${featuredImageTab === 'ai' ? 'active' : ''}`}
                                onClick={() => setFeaturedImageTab('ai')}
                            >
                                <Sparkles size={16} />
                                AI Generate
                            </button>
                        </div>

                        <div className="image-upload-area">
                            {/* Show preview if image is selected */}
                            {formData.featuredImage && (
                                <div className="image-preview">
                                    <img
                                        src={
                                            (() => {
                                                // Handle both string and object format
                                                const imageUrl = typeof formData.featuredImage === 'string'
                                                    ? formData.featuredImage
                                                    : formData.featuredImage?.url || '';

                                                if (!imageUrl) return '';

                                                if (imageUrl.startsWith('http') ||
                                                    imageUrl.startsWith('blob:') ||
                                                    imageUrl.startsWith('/api')) {
                                                    return imageUrl;
                                                }

                                                // Hardcoded API URL based on hostname
                                                const hostname = window.location.hostname;
                                                let API_BASE_URL;

                                                // Production domains
                                                if (hostname === 'lima.ac' || hostname.includes('lima.ac')) {
                                                    API_BASE_URL = 'https://lima.ac';
                                                } else if (hostname === 'lima.pitech.ca' || hostname.includes('lima.pitech.ca')) {
                                                    API_BASE_URL = 'https://lima.ac';
                                                } else if (hostname === 'bagbox.ca' || hostname.includes('bagbox.ca')) {
                                                    API_BASE_URL = 'https://bagbox.ca';
                                                } else {
                                                    // Development/localhost
                                                    API_BASE_URL = 'http://localhost:80';
                                                }

                                                return `${API_BASE_URL}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
                                            })()
                                        }
                                        alt="Featured"
                                        style={{ maxWidth: '100%', height: 'auto', display: 'block', borderRadius: '8px' }}
                                        onError={(e) => {
                                            console.error('❌ Image failed to load:', formData.featuredImage);
                                            console.error('Full URL attempted:', e.target.src);
                                        }}
                                        onLoad={() => {
                                            console.log('✅ Image loaded successfully:', formData.featuredImage);
                                        }}
                                    />
                                    <button
                                        onClick={() => setFormData({ ...formData, featuredImage: '' })}
                                        className="remove-image-btn"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            )}
                            {!formData.featuredImage && (
                                <p style={{ color: '#666', padding: '2rem', textAlign: 'center', fontStyle: 'italic' }}>
                                    No image selected - choose from tabs above
                                </p>
                            )}

                            {/* Upload Tab */}
                            {featuredImageTab === 'upload' && !formData.featuredImage && (
                                <>
                                    <label htmlFor="featured-image-upload" className="upload-dropzone" style={{ cursor: 'pointer' }}>
                                        <Upload size={48} />
                                        <h3>Upload Image</h3>
                                        <p>Click to browse or drag and drop</p>
                                    </label>
                                    <input
                                        id="featured-image-upload"
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                handleFileUpload(file);
                                            }
                                        }}
                                        style={{ display: 'none' }}
                                    />
                                    <div className="url-divider">OR</div>
                                    <div className="url-input-container">
                                        <input
                                            type="text"
                                            value={imageUrl}
                                            onChange={(e) => setImageUrl(e.target.value)}
                                            placeholder="Image URL"
                                            className="url-input"
                                        />
                                        <button onClick={handleImageUrlAdd} className="add-url-btn">
                                            <Plus size={20} />
                                        </button>
                                    </div>
                                </>
                            )}

                            {/* Library Tab - Simplified */}
                            {featuredImageTab === 'library' && !formData.featuredImage && (
                                <div className="media-library-grid">
                                    <div className="no-media-message">
                                        <ImageIcon size={48} />
                                        <p>Media library not available in this view</p>
                                        <p className="text-xs text-gray-500">Use AI Generate Post to create images</p>
                                    </div>
                                </div>
                            )}

                            {/* AI Generate Tab - With predefined prompts */}
                            {featuredImageTab === 'ai' && !formData.featuredImage && (
                                <div className="ai-image-generator">
                                    <div className="ai-prompt-input">
                                        <input
                                            type="text"
                                            value={aiImagePrompt}
                                            onChange={(e) => setAiImagePrompt(e.target.value)}
                                            placeholder="Describe the image you want to generate..."
                                            className="url-input"
                                            style={{ marginBottom: '1rem' }}
                                        />
                                        <button
                                            onClick={() => handleGenerateFeaturedImage(aiImagePrompt)}
                                            className="btn-primary"
                                            disabled={generatingImage || !aiImagePrompt.trim()}
                                            style={{ width: '100%', marginBottom: '1.5rem' }}
                                        >
                                            {generatingImage ? (
                                                <>
                                                    <Loader2 size={16} className="animate-spin" />
                                                    Generating...
                                                </>
                                            ) : (
                                                <>
                                                    <Sparkles size={16} />
                                                    Generate Image
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    <div className="url-divider">OR CHOOSE A STYLE</div>

                                    <div className="predefined-prompts" style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(2, 1fr)',
                                        gap: '0.75rem',
                                        marginTop: '1rem'
                                    }}>
                                        <button
                                            onClick={() => handleGenerateFeaturedImage(`Professional business blog header image for: ${formData.title || 'modern technology'}`)}
                                            className="predefined-prompt-btn"
                                            disabled={generatingImage}
                                            style={{
                                                padding: '1rem',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                background: 'white',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                textAlign: 'left'
                                            }}
                                        >
                                            <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Professional</div>
                                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Clean & business-focused</div>
                                        </button>

                                        <button
                                            onClick={() => handleGenerateFeaturedImage(`Modern minimalist header image for blog about: ${formData.title || 'technology'}, clean design, vibrant colors`)}
                                            className="predefined-prompt-btn"
                                            disabled={generatingImage}
                                            style={{
                                                padding: '1rem',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                background: 'white',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                textAlign: 'left'
                                            }}
                                        >
                                            <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Modern</div>
                                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Minimalist & vibrant</div>
                                        </button>

                                        <button
                                            onClick={() => handleGenerateFeaturedImage(`Abstract artistic representation of: ${formData.title || 'innovation'}, creative, colorful, inspiring`)}
                                            className="predefined-prompt-btn"
                                            disabled={generatingImage}
                                            style={{
                                                padding: '1rem',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                background: 'white',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                textAlign: 'left'
                                            }}
                                        >
                                            <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Abstract</div>
                                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Creative & artistic</div>
                                        </button>

                                        <button
                                            onClick={() => handleGenerateFeaturedImage(`Photorealistic image related to: ${formData.title || 'technology'}, high quality, detailed, professional photography`)}
                                            className="predefined-prompt-btn"
                                            disabled={generatingImage}
                                            style={{
                                                padding: '1rem',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                background: 'white',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                textAlign: 'left'
                                            }}
                                        >
                                            <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Photorealistic</div>
                                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>High-quality & detailed</div>
                                        </button>

                                        <button
                                            onClick={() => handleGenerateFeaturedImage(`3D rendered illustration for blog about: ${formData.title || 'technology'}, modern 3D design, vibrant colors`)}
                                            className="predefined-prompt-btn"
                                            disabled={generatingImage}
                                            style={{
                                                padding: '1rem',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                background: 'white',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                textAlign: 'left'
                                            }}
                                        >
                                            <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>3D Illustration</div>
                                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Modern & eye-catching</div>
                                        </button>

                                        <button
                                            onClick={() => handleGenerateFeaturedImage(`Flat design illustration for: ${formData.title || 'technology'}, simple, clean, modern vector art`)}
                                            className="predefined-prompt-btn"
                                            disabled={generatingImage}
                                            style={{
                                                padding: '1rem',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                background: 'white',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                textAlign: 'left'
                                            }}
                                        >
                                            <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Flat Design</div>
                                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Simple & clean vector</div>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* SEO Section */}
                    <div className="form-section">
                        <div className="section-header">
                            <label>SEO Optimization</label>
                            <button
                                className="ai-generate-btn"
                                onClick={handleGenerateSEO}
                                disabled={generatingSEOTitle}
                            >
                                {generatingSEOTitle ? (
                                    <><Loader2 size={16} className="animate-spin" /> Generating...</>
                                ) : (
                                    <><Sparkles size={16} /> AI Generate</>
                                )}
                            </button>
                        </div>

                        <div className="seo-fields">
                            <div className="form-group">
                                <label>SEO Title</label>
                                <input
                                    type="text"
                                    value={formData.seo.metaTitle}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        seo: { ...formData.seo, metaTitle: e.target.value }
                                    })}
                                    placeholder="SEO Title"
                                    className="form-input"
                                />
                                <div className="char-count">{seoTitleCharCount}/60 characters</div>
                            </div>

                            <div className="form-group">
                                <label>SEO Description</label>
                                <textarea
                                    value={formData.seo.metaDescription}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        seo: { ...formData.seo, metaDescription: e.target.value }
                                    })}
                                    placeholder="Description that appears in search results..."
                                    className="form-textarea"
                                    rows={3}
                                />
                                <div className="char-count">{seoDescCharCount}/160 characters (optimal: 120-160)</div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Focus Keywords</label>
                                    <div className="keywords-input-container">
                                        <input
                                            type="text"
                                            value={newKeyword}
                                            onChange={(e) => setNewKeyword(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddKeyword())}
                                            placeholder="Add Focus Keyword"
                                            className="keyword-input"
                                        />
                                        <button onClick={handleAddKeyword} className="add-keyword-btn">
                                            <Plus size={20} />
                                        </button>
                                    </div>
                                    {formData.seo.keywords.length > 0 && (
                                        <div className="keywords-list">
                                            {formData.seo.keywords.map((kw, index) => (
                                                <span key={index} className="keyword-item">
                                                    {kw}
                                                    <button onClick={() => handleRemoveKeyword(kw)} className="keyword-remove">×</button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label>URL Slug</label>
                                    <input
                                        type="text"
                                        value={formData.seo.slug}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            seo: { ...formData.seo, slug: e.target.value }
                                        })}
                                        placeholder="URL Slug"
                                        className="form-input"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* AI-Powered Analysis Button */}
                    <button
                        className="ai-analysis-btn"
                        onClick={handleAnalyzeCurrentPost}
                        disabled={analyzingComprehensive || !formData.title || !formData.content}
                    >
                        {analyzingComprehensive ? (
                            <>
                                <Loader2 size={20} className="animate-spin" />
                                Analyzing...
                            </>
                        ) : (
                            <>
                                <BarChart3 size={20} />
                                AI-Powered Analysis
                            </>
                        )}
                    </button>

                    {/* Comprehensive Analysis Display */}
                    {comprehensiveAnalysis && !analyzingComprehensive && comprehensiveAnalysis.summary && (
                        <div data-comprehensive-analysis className="comprehensive-analysis-section">
                            <div className="analysis-header">
                                <div className="analysis-title">
                                    <BarChart3 size={20} />
                                    <div>
                                        <h4>AI Quality Analysis</h4>
                                        <p>Comprehensive evaluation of your post</p>
                                    </div>
                                </div>
                                <div className="analysis-status-badge">
                                    {comprehensiveAnalysis.summary?.overallScore >= 80 ? 'Excellent' :
                                        comprehensiveAnalysis.summary?.overallScore >= 60 ? 'Good' : 'Needs Work'}
                                </div>
                            </div>

                            {/* Overall Score */}
                            <div className="analysis-score">
                                <div
                                    className="score-value"
                                    style={{
                                        color: comprehensiveAnalysis.summary.overallScore >= 80 ? '#10b981' :
                                            comprehensiveAnalysis.summary.overallScore >= 60 ? '#f59e0b' : '#ef4444'
                                    }}
                                >
                                    {comprehensiveAnalysis.summary.overallScore}
                                </div>
                                <div className="score-label">Overall Quality Score</div>
                            </div>

                            {/* Key Metrics */}
                            <div className="analysis-metrics">
                                <h5>
                                    <TrendingUp size={16} />
                                    Key Metrics
                                </h5>
                                <div className="metrics-grid">
                                    {/* Title Metric */}
                                    <div
                                        className="metric-card"
                                        style={{
                                            borderColor: comprehensiveAnalysis.seoOptimization?.title?.score >= 90 ? '#d1fae5' :
                                                comprehensiveAnalysis.seoOptimization?.title?.score >= 70 ? '#fef3c7' : '#fee2e2'
                                        }}
                                    >
                                        <span className="metric-label">Title</span>
                                        <span
                                            className="metric-value"
                                            style={{
                                                color: comprehensiveAnalysis.seoOptimization?.title?.score >= 90 ? '#10b981' :
                                                    comprehensiveAnalysis.seoOptimization?.title?.score >= 70 ? '#f59e0b' : '#ef4444'
                                            }}
                                        >
                                            {comprehensiveAnalysis.seoOptimization?.title?.score || 0}
                                        </span>
                                    </div>

                                    {/* Content Metric */}
                                    <div
                                        className="metric-card"
                                        style={{
                                            borderColor: comprehensiveAnalysis.contentQuality?.structure?.wordCount >= 1500 ? '#d1fae5' : '#fee2e2'
                                        }}
                                    >
                                        <span className="metric-label">Content</span>
                                        <span
                                            className="metric-value"
                                            style={{
                                                color: comprehensiveAnalysis.contentQuality?.structure?.wordCount >= 1500 ? '#10b981' : '#ef4444'
                                            }}
                                        >
                                            {comprehensiveAnalysis.contentQuality?.structure?.wordCount || 0}w
                                        </span>
                                    </div>

                                    {/* Meta Metric */}
                                    <div
                                        className="metric-card"
                                        style={{
                                            borderColor: comprehensiveAnalysis.seoOptimization?.metaDescription?.score >= 90 ? '#d1fae5' :
                                                comprehensiveAnalysis.seoOptimization?.metaDescription?.score >= 70 ? '#fef3c7' : '#fee2e2'
                                        }}
                                    >
                                        <span className="metric-label">Meta</span>
                                        <span
                                            className="metric-value"
                                            style={{
                                                color: comprehensiveAnalysis.seoOptimization?.metaDescription?.score >= 90 ? '#10b981' :
                                                    comprehensiveAnalysis.seoOptimization?.metaDescription?.score >= 70 ? '#f59e0b' : '#ef4444'
                                            }}
                                        >
                                            {comprehensiveAnalysis.seoOptimization?.metaDescription?.score || 0}
                                        </span>
                                    </div>

                                    {/* Links Metric */}
                                    <div
                                        className="metric-card"
                                        style={{
                                            borderColor: comprehensiveAnalysis.contentQuality?.links?.score >= 90 ? '#d1fae5' :
                                                comprehensiveAnalysis.contentQuality?.links?.score >= 70 ? '#fef3c7' : '#fee2e2'
                                        }}
                                    >
                                        <span className="metric-label">Links</span>
                                        <span
                                            className="metric-value"
                                            style={{
                                                color: comprehensiveAnalysis.contentQuality?.links?.score >= 90 ? '#10b981' :
                                                    comprehensiveAnalysis.contentQuality?.links?.score >= 70 ? '#f59e0b' : '#ef4444'
                                            }}
                                        >
                                            {comprehensiveAnalysis.contentQuality?.links?.score || 0}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Top Recommendations */}
                            {(comprehensiveAnalysis.recommendations?.critical?.length > 0 ||
                                comprehensiveAnalysis.recommendations?.high?.length > 0) && (
                                    <div className="analysis-recommendations">
                                        <h5>
                                            <Sparkles size={16} />
                                            Top Recommendations
                                        </h5>
                                        <ul>
                                            {[
                                                ...(comprehensiveAnalysis.recommendations.critical || []),
                                                ...(comprehensiveAnalysis.recommendations.high || []).slice(0, 2)
                                            ].map((rec, index) => (
                                                <li key={index}>
                                                    <span className="rec-number">#{index + 1}</span>
                                                    <span>{rec}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        {/* Improve Button */}
                                        <button
                                            onClick={handleImproveCurrentContent}
                                            disabled={improvingContent}
                                            className="improve-content-btn"
                                        >
                                            {improvingContent ? (
                                                <>
                                                    <Loader2 size={18} className="animate-spin" />
                                                    Improving Content...
                                                </>
                                            ) : (
                                                <>
                                                    Improve Content
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                        </div>
                    )}
                </div>

                {/* Footer Actions */}
                <div className="blog-editor-footer">
                    <button onClick={() => handleSave('draft')} className="btn-draft" disabled={saving}>
                        <Save size={18} />
                        Save Draft
                    </button>
                    <div className="footer-actions-right">
                        <button onClick={() => handleSave('scheduled')} className="btn-schedule" disabled={saving}>
                            <Calendar size={18} />
                            Schedule
                        </button>
                        <button onClick={() => handleSave('published')} className="btn-publish" disabled={saving}>
                            <FileText size={18} />
                            {saving ? 'Publishing...' : 'Publish'}
                        </button>
                    </div>
                </div>
            </div>

            {/* AI Generate Modal */}
            {showAIModal && (
                <div className="ai-modal-overlay" onClick={() => !generatingAI && setShowAIModal(false)}>
                    <div className="ai-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="ai-modal-header">
                            <div>
                                <h2>
                                    <Sparkles size={24} />
                                    AI Post Generator
                                </h2>
                            </div>
                            <button onClick={() => setShowAIModal(false)} className="ai-modal-close">
                                <X size={24} />
                            </button>
                        </div>

                        {!generatedPostPreview ? (
                            /* Prompt Input */
                            <>
                                <div className="ai-modal-content">
                                    <label>Describe the post you want to create</label>
                                    <textarea
                                        value={aiPrompt}
                                        onChange={(e) => setAIPrompt(e.target.value)}
                                        placeholder="Example: Write a comprehensive guide about the benefits of meditation for beginners, including practical tips and scientific research..."
                                        rows={6}
                                        className="ai-prompt-textarea"
                                        disabled={generatingAI}
                                    />

                                    {/* Show Last Accepted Post Option */}
                                    {lastAcceptedPost && !post?._id && (
                                        <div className="last-accepted-post-card">
                                            <div className="last-accepted-post-content">
                                                <div className="last-accepted-post-header">
                                                    <CheckCircle size={20} />
                                                    <h4>Last Accepted Post</h4>
                                                </div>
                                                <p className="last-accepted-post-title">{lastAcceptedPost.title}</p>
                                                <p className="last-accepted-post-date">
                                                    Accepted {new Date(lastAcceptedPost.acceptedAt).toLocaleString()}
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => setGeneratedPostPreview(lastAcceptedPost)}
                                                className="view-post-btn"
                                            >
                                                View Post
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div className="ai-modal-footer">
                                    <button
                                        onClick={() => {
                                            setShowAIModal(false);
                                            setAIPrompt('');
                                        }}
                                        disabled={generatingAI}
                                        className="ai-modal-btn-cancel"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleAIGeneratePost}
                                        disabled={generatingAI || !aiPrompt.trim()}
                                        className="ai-modal-btn-generate"
                                    >
                                        {generatingAI ? (
                                            <>
                                                <Loader2 size={18} className="animate-spin" />
                                                Generating...
                                            </>
                                        ) : (
                                            <>
                                                <Sparkles size={18} />
                                                Generate Post
                                            </>
                                        )}
                                    </button>
                                </div>
                            </>
                        ) : (
                            /* Post Preview with AI Analysis */
                            <>
                                <div className="ai-modal-content ai-preview">
                                    {/* Title & Meta */}
                                    <div className="ai-preview-title-section">
                                        <h2 className="ai-preview-title">{generatedPostPreview.title}</h2>
                                        <div className="ai-preview-meta">
                                            <span>{generatedPostPreview.readTime || '5'} min read</span>
                                            <span>{new Date().toLocaleDateString()}</span>
                                            <span>{generatedPostPreview.tags?.length || 0} tags</span>
                                        </div>
                                    </div>

                                    {/* Excerpt */}
                                    <div className="ai-preview-excerpt">
                                        <h4>Excerpt:</h4>
                                        <p>{generatedPostPreview.excerpt}</p>
                                    </div>

                                    {/* Content Preview with Image Controls */}
                                    <div className="ai-preview-content-section">
                                        <div className="ai-preview-content-header">
                                            <h4>Content Preview:</h4>
                                            <span className="ai-preview-image-count">
                                                {(generatedPostPreview.content?.match(/<img/g) || []).length} images detected
                                            </span>
                                        </div>

                                        {/* Parse and render content with image controls */}
                                        <div className="ai-preview-html-content prose prose-lg" ref={contentPreviewRef}>
                                            {(() => {
                                                const imageKeys = ['hero', 'img1', 'img2', 'img3', 'img4', 'img5', 'img6', 'img7'];

                                                // Clone the content and add regenerate buttons to figures
                                                const processedContent = generatedPostPreview.content.replace(
                                                    /<figure[^>]*>([\s\S]*?)<\/figure>/gi,
                                                    (match, figContent, offset) => {
                                                        const figIndex = (generatedPostPreview.content.substring(0, offset).match(/<figure/gi) || []).length;
                                                        const imageKey = imageKeys[figIndex] || `img${figIndex}`;
                                                        const history = imageHistory[imageKey] || [];

                                                        return `
                                                          <div class="image-with-controls" data-image-key="${imageKey}">
                                                            <figure class="relative">${figContent}</figure>
                                                            <div class="image-controls">
                                                              <button 
                                                                type="button"
                                                                class="regenerate-btn"
                                                                title="Regenerate this image (uses 1 AI image quota)"
                                                              >
                                                                <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                                </svg>
                                                                <span>${regeneratingImage === imageKey ? 'Regenerating...' : 'Regenerate'}</span>
                                                              </button>
                                                              ${history.length > 0 ? `
                                                                <button 
                                                                  type="button"
                                                                  class="history-btn"
                                                                  title="View previous versions (${history.length})"
                                                                >
                                                                  <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                  </svg>
                                                                  <span>${history.length}</span>
                                                                </button>
                                                              ` : ''}
                                                            </div>
                                                          </div>
                                                        `;
                                                    }
                                                );

                                                return <div dangerouslySetInnerHTML={{ __html: processedContent }} />;
                                            })()}
                                        </div>
                                    </div>

                                    {/* Tags */}
                                    {generatedPostPreview.tags && generatedPostPreview.tags.length > 0 && (
                                        <div className="ai-preview-tags-section">
                                            <h4>Generated Tags:</h4>
                                            <div className="ai-preview-tags">
                                                {generatedPostPreview.tags.map((tag, i) => (
                                                    <span key={i} className="preview-tag">{tag}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Analyze Button */}
                                    {!generatedPostAnalysis && !analyzingGeneratedPost && (
                                        <div className="analyze-button-container">
                                            <button
                                                onClick={() => handleAnalyzeGeneratedPost(generatedPostPreview)}
                                                className="analyze-btn"
                                            >
                                                <BarChart3 size={20} />
                                                Analyze Post Quality
                                            </button>
                                        </div>
                                    )}

                                    {/* Analyzing Loader */}
                                    {analyzingGeneratedPost && (
                                        <div className="analyzing-loader">
                                            <Loader2 size={24} className="animate-spin" />
                                            <span>Analyzing your generated post with AI...</span>
                                        </div>
                                    )}

                                    {/* Comprehensive AI-Powered Analysis */}
                                    {generatedPostAnalysis && !analyzingGeneratedPost && generatedPostAnalysis.summary && (
                                        <div data-analysis-section className="analysis-section">
                                            <div className="analysis-header">
                                                <div className="analysis-title">
                                                    <BarChart3 size={20} />
                                                    <div>
                                                        <h4>AI Quality Analysis</h4>
                                                        <p>Comprehensive evaluation of your generated post</p>
                                                    </div>
                                                </div>
                                                <div className="analysis-status-badge">
                                                    {generatedPostAnalysis.summary?.overallScore >= 80 ? 'Excellent' :
                                                        generatedPostAnalysis.summary?.overallScore >= 60 ? 'Good' : 'Needs Work'}
                                                </div>
                                            </div>

                                            {/* Overall Score */}
                                            <div className="analysis-score">
                                                <div
                                                    className="score-value"
                                                    style={{
                                                        color: generatedPostAnalysis.summary.overallScore >= 80 ? '#10b981' :
                                                            generatedPostAnalysis.summary.overallScore >= 60 ? '#f59e0b' : '#ef4444'
                                                    }}
                                                >
                                                    {generatedPostAnalysis.summary.overallScore}
                                                </div>
                                                <div className="score-label">Overall Quality Score</div>
                                            </div>

                                            {/* Key Metrics */}
                                            <div className="analysis-metrics">
                                                <h5>
                                                    <TrendingUp size={16} />
                                                    Metrics Analysis
                                                </h5>
                                                <div className="metrics-grid">
                                                    {/* Title Metric */}
                                                    <div
                                                        className="metric-card"
                                                        style={{
                                                            borderColor: generatedPostAnalysis.seoOptimization?.title?.score >= 90 ? '#d1fae5' :
                                                                generatedPostAnalysis.seoOptimization?.title?.score >= 70 ? '#fef3c7' : '#fee2e2'
                                                        }}
                                                    >
                                                        <span className="metric-label">Title</span>
                                                        <span
                                                            className="metric-value"
                                                            style={{
                                                                color: generatedPostAnalysis.seoOptimization?.title?.score >= 90 ? '#10b981' :
                                                                    generatedPostAnalysis.seoOptimization?.title?.score >= 70 ? '#f59e0b' : '#ef4444'
                                                            }}
                                                        >
                                                            {generatedPostAnalysis.seoOptimization?.title?.score || 0}
                                                        </span>
                                                    </div>

                                                    {/* Content Metric */}
                                                    <div
                                                        className="metric-card"
                                                        style={{
                                                            borderColor: generatedPostAnalysis.contentQuality?.structure?.wordCount >= 1500 ? '#d1fae5' : '#fee2e2'
                                                        }}
                                                    >
                                                        <span className="metric-label">Content</span>
                                                        <span
                                                            className="metric-value"
                                                            style={{
                                                                color: generatedPostAnalysis.contentQuality?.structure?.wordCount >= 1500 ? '#10b981' : '#ef4444'
                                                            }}
                                                        >
                                                            {generatedPostAnalysis.contentQuality?.structure?.wordCount || 0}w
                                                        </span>
                                                    </div>

                                                    {/* Meta Metric */}
                                                    <div
                                                        className="metric-card"
                                                        style={{
                                                            borderColor: generatedPostAnalysis.seoOptimization?.metaDescription?.score >= 90 ? '#d1fae5' :
                                                                generatedPostAnalysis.seoOptimization?.metaDescription?.score >= 70 ? '#fef3c7' : '#fee2e2'
                                                        }}
                                                    >
                                                        <span className="metric-label">Meta</span>
                                                        <span
                                                            className="metric-value"
                                                            style={{
                                                                color: generatedPostAnalysis.seoOptimization?.metaDescription?.score >= 90 ? '#10b981' :
                                                                    generatedPostAnalysis.seoOptimization?.metaDescription?.score >= 70 ? '#f59e0b' : '#ef4444'
                                                            }}
                                                        >
                                                            {generatedPostAnalysis.seoOptimization?.metaDescription?.score || 0}
                                                        </span>
                                                    </div>

                                                    {/* Links Metric */}
                                                    <div
                                                        className="metric-card"
                                                        style={{
                                                            borderColor: generatedPostAnalysis.contentQuality?.links?.score >= 90 ? '#d1fae5' :
                                                                generatedPostAnalysis.contentQuality?.links?.score >= 70 ? '#fef3c7' : '#fee2e2'
                                                        }}
                                                    >
                                                        <span className="metric-label">Links</span>
                                                        <span
                                                            className="metric-value"
                                                            style={{
                                                                color: generatedPostAnalysis.contentQuality?.links?.score >= 90 ? '#10b981' :
                                                                    generatedPostAnalysis.contentQuality?.links?.score >= 70 ? '#f59e0b' : '#ef4444'
                                                            }}
                                                        >
                                                            {generatedPostAnalysis.contentQuality?.links?.score || 0}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Top Recommendations */}
                                            {(generatedPostAnalysis.recommendations?.critical?.length > 0 ||
                                                generatedPostAnalysis.recommendations?.high?.length > 0) && (
                                                    <div className="analysis-recommendations">
                                                        <h5>
                                                            <Sparkles size={16} />
                                                            Top Recommendations
                                                        </h5>
                                                        <ul>
                                                            {[
                                                                ...(generatedPostAnalysis.recommendations.critical || []),
                                                                ...(generatedPostAnalysis.recommendations.high || []).slice(0, 2)
                                                            ].map((rec, index) => (
                                                                <li key={index}>
                                                                    <span className="rec-number">#{index + 1}</span>
                                                                    <span>{rec}</span>
                                                                </li>
                                                            ))}
                                                        </ul>

                                                        {/* Improve Button */}
                                                        <button
                                                            onClick={handleImproveContent}
                                                            disabled={improvingContent}
                                                            className="improve-content-btn"
                                                        >
                                                            {improvingContent ? (
                                                                <>
                                                                    <Loader2 size={18} className="animate-spin" />
                                                                    Improving Content...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Sparkles size={18} />
                                                                    Improve Content
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                )}
                                        </div>
                                    )}
                                </div>

                                <div className="ai-modal-footer ai-preview-footer">
                                    <button
                                        onClick={handleRegenerate}
                                        className="ai-modal-btn-regenerate"
                                    >
                                        Try Again
                                    </button>
                                    <div className="ai-modal-actions">
                                        <button
                                            onClick={() => {
                                                setGeneratedPostPreview(null);
                                                setGeneratedPostAnalysis(null);
                                                setMetricImprovements({});
                                            }}
                                            className="ai-modal-btn-decline"
                                        >
                                            Decline & Discard
                                        </button>
                                        <button
                                            onClick={handleAcceptAIPost}
                                            className="ai-modal-btn-accept"
                                        >
                                            Accept
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Image History Modal */}
            {imageHistoryModal && imageHistoryModal.isOpen && (
                <div className="modal-overlay" onClick={() => setImageHistoryModal(null)}>
                    <div className="modal-content image-history-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div>
                                <h3>Image History</h3>
                                <p className="modal-subtitle">
                                    Select a previous version to restore (Image: {imageHistoryModal.imageKey.toUpperCase()})
                                </p>
                            </div>
                            <button
                                onClick={() => setImageHistoryModal(null)}
                                className="modal-close-btn"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="modal-body">
                            {/* Current Image */}
                            <div className="current-image-card">
                                <div className="current-image-header">
                                    <div className="current-indicator"></div>
                                    <h4>Current Image</h4>
                                </div>
                                <img
                                    src={imageHistoryModal.currentImageUrl}
                                    alt="Current version"
                                    className="current-image"
                                    onError={(e) => {
                                        console.error('Failed to load current image:', imageHistoryModal.currentImageUrl);
                                        e.currentTarget.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><text x="50%" y="50%" text-anchor="middle">Error</text></svg>';
                                    }}
                                />
                            </div>

                            {/* Previous Versions */}
                            <div className="previous-versions">
                                <h4 className="previous-versions-title">
                                    Previous Versions ({imageHistory[imageHistoryModal.imageKey]?.length || 0})
                                </h4>
                                {(!imageHistory[imageHistoryModal.imageKey] || imageHistory[imageHistoryModal.imageKey].length === 0) ? (
                                    <div className="no-history">
                                        <p>No previous versions yet.</p>
                                        <p className="no-history-hint">Regenerate this image to create history.</p>
                                    </div>
                                ) : (
                                    <div className="history-grid">
                                        {imageHistory[imageHistoryModal.imageKey]?.slice().reverse().map((url, index) => {
                                            const originalLength = imageHistory[imageHistoryModal.imageKey].length;
                                            const versionNumber = originalLength - index;

                                            return (
                                                <div key={index} className="history-item">
                                                    <img
                                                        src={url}
                                                        alt={`Version ${versionNumber}`}
                                                        className="history-image"
                                                        onError={(e) => {
                                                            console.error('Failed to load history image:', url);
                                                            e.currentTarget.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><text x="50%" y="50%" text-anchor="middle">Error</text></svg>';
                                                        }}
                                                    />
                                                    <div className="history-item-footer">
                                                        <span className="version-label">Version {versionNumber}</span>
                                                        <button
                                                            onClick={() => handleRestoreImageVersion(imageHistoryModal.imageKey, url)}
                                                            className="restore-btn"
                                                        >
                                                            Restore
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button
                                onClick={() => setImageHistoryModal(null)}
                                className="modal-btn-secondary"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Subscription Modal Placeholder - will be added */}
            {showSubscriptionModal && (
                <div className="modal-overlay" onClick={() => setShowSubscriptionModal(false)}>
                    <div className="modal-content subscription-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Upgrade Required</h3>
                            <button onClick={() => setShowSubscriptionModal(false)} className="modal-close-btn">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>{subscriptionMessage}</p>
                            <p>Please upgrade your subscription to continue using AI features.</p>
                        </div>
                        <div className="modal-footer">
                            <button
                                onClick={() => setShowSubscriptionModal(false)}
                                className="modal-btn-secondary"
                            >
                                Close
                            </button>
                            <button className="modal-btn-primary">
                                View Plans
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BlogPostEditor;

